/**
 * Pattern Matcher
 *
 * Matches tokenized input against language patterns to extract semantic roles.
 * This is the core algorithm for multilingual parsing.
 */

import type {
  LanguagePattern,
  PatternToken,
  PatternMatchResult,
  ReferenceValue,
  SemanticRole,
  SemanticValue,
  TokenStream,
  LanguageToken,
} from '../types';
import {
  createSelector,
  createLiteral,
  createReference,
  createPropertyPath,
  isValidReference,
} from '../types';
import { stripOptionalDiacritics } from '@lokascript/framework';
import { isTypeCompatible } from './utils/type-validation';
import { commandSchemas, type CommandSchema } from '../generators/command-schemas';
import { getPossessiveReference } from './utils/possessive-keywords';
import {
  COMMAND_ACTION_KEYWORDS,
  isOfPossessiveMarker,
  joinExpressionTokens,
  matchPositionalRun,
  translatePropertyName,
} from './utils/expression-lexicon';
import type { LanguageProfile } from '../generators/profiles/types';
import { tryGetProfile } from '../registry';
import { isAtEndConnective } from '../patterns/put';
import { isCuratedEndKeyword } from './end-keywords';
import { foldNakedNamedArgsRaw } from './naked-args-fold';
import type { ConfidenceModel } from './confidence-model';
import { defaultConfidenceModel } from './confidence-model';

/**
 * Body-bearing command keywords that front their own block (`bareKeyword` schemas:
 * `live`/`eventsource`/`socket`/`worker`/`intercept`). The keyword leads the
 * construct (`live put … end`, `socket Name url on message … end`) and is NEVER an
 * event name, so it must not anchor a bare-event pattern at Stage 1 — see
 * tokenLooksLikeEvent. Derived from the schemas so a new bareKeyword block is
 * covered automatically.
 */
const BAREKEYWORD_BLOCK_ACTIONS: ReadonlySet<string> = new Set(
  Object.values(commandSchemas)
    .filter(s => s.bareKeyword === true)
    .map(s => s.action)
);

// =============================================================================
// Pattern Matcher
// =============================================================================

export class PatternMatcher {
  /** Current language profile for the pattern being matched */
  private currentProfile: LanguageProfile | undefined;
  /**
   * Command of the pattern currently being matched. Used to scope the
   * event-anchor guard to event-HANDLER patterns (`on`) — the `event` role on
   * `send`/`trigger` command patterns is a payload that CAN be a string literal
   * (`send "hello"`) or identifier (`trigger refresh`), and must not be gated.
   */
  private currentPatternCommand: string | undefined;
  /** Injectable confidence scoring model (Phase 3.3) */
  private readonly confidenceModel: ConfidenceModel;
  /**
   * Selective memoization cache (Phase 6.1).
   * Caches pattern match results keyed by `tokenPosition:patternId`.
   * Cleared before each top-level matchBest() call.
   */
  private matchCache = new Map<string, PatternMatchResult | null>();

  constructor(confidenceModel?: ConfidenceModel) {
    this.confidenceModel = confidenceModel ?? defaultConfidenceModel;
  }

  /**
   * Try to match a single pattern against the token stream.
   * Returns the match result or null if no match.
   */
  matchPattern(tokens: TokenStream, pattern: LanguagePattern): PatternMatchResult | null {
    const mark = tokens.mark();
    const captured = new Map<SemanticRole, SemanticValue>();

    // Get language profile for possessive keyword lookup
    this.currentProfile = tryGetProfile(pattern.language);
    this.currentPatternCommand = pattern.command;

    // Reset match counters for this pattern
    this.stemMatchCount = 0;
    this.totalKeywordMatches = 0;

    const success = this.matchTokenSequence(tokens, pattern.template.tokens, captured);

    if (!success) {
      tokens.reset(mark);
      return null;
    }

    // Calculate confidence BEFORE applying defaults
    // This ensures defaulted roles don't artificially inflate confidence
    const confidence = this.confidenceModel.calculate({
      pattern,
      captured,
      stemMatchCount: this.stemMatchCount,
      totalKeywordMatches: this.totalKeywordMatches,
    });

    // Apply extraction rules to fill in any missing roles with defaults
    this.applyExtractionRules(pattern, captured);

    return {
      pattern,
      captured,
      consumedTokens: tokens.position() - mark.position,
      confidence,
    };
  }

  /**
   * Try to match multiple patterns, return the best match.
   */
  matchBest(tokens: TokenStream, patterns: LanguagePattern[]): PatternMatchResult | null {
    // Clear memoization cache for this matching round (Phase 6.1)
    this.matchCache.clear();

    const matches: PatternMatchResult[] = [];
    const startPos = tokens.position();

    for (const pattern of patterns) {
      const mark = tokens.mark();

      // Check memoization cache (Phase 6.1)
      const cacheKey = `${startPos}:${pattern.id}`;
      let result: PatternMatchResult | null;

      if (this.matchCache.has(cacheKey)) {
        result = this.matchCache.get(cacheKey)!;
      } else {
        result = this.matchPattern(tokens, pattern);
        this.matchCache.set(cacheKey, result);
      }

      if (result) {
        matches.push(result);
      }

      tokens.reset(mark);
    }

    if (matches.length === 0) {
      return null;
    }

    // Sort by confidence and priority
    matches.sort((a, b) => {
      // First by priority
      const priorityDiff = b.pattern.priority - a.pattern.priority;
      if (priorityDiff !== 0) return priorityDiff;

      // Then by confidence
      return b.confidence - a.confidence;
    });

    // Re-consume tokens for the best match
    const best = matches[0];
    this.matchPattern(tokens, best.pattern);

    return best;
  }

  /**
   * Match a sequence of pattern tokens against the token stream.
   */
  private matchTokenSequence(
    tokens: TokenStream,
    patternTokens: PatternToken[],
    captured: Map<SemanticRole, SemanticValue>,
    // What follows the sequence itself — a GROUP's last inner token must see the
    // token after the group as its `next` (the trailing-slot verb guard in
    // matchRoleToken keys on nextPatternToken === undefined meaning "pattern
    // truly ends here", and a mid-pattern single-token group is not the end).
    nextAfterSequence?: PatternToken
  ): boolean {
    // Skip leading conjunctions for Arabic (proclitics: و, ف, ول, وب, etc.)
    // BUT NOT if the pattern explicitly expects a conjunction (proclitic patterns)
    const firstPatternToken = patternTokens[0];
    const patternExpectsConjunction =
      firstPatternToken?.type === 'literal' &&
      (firstPatternToken.value === 'and' ||
        firstPatternToken.value === 'then' ||
        firstPatternToken.alternatives?.includes('and') ||
        firstPatternToken.alternatives?.includes('then'));

    if (this.currentProfile?.code === 'ar' && !patternExpectsConjunction) {
      while (tokens.peek()?.kind === 'conjunction') {
        tokens.advance();
      }
    }

    // Source-clause window: after the event role matches, the next 1–2 pattern
    // tokens may be preceded in the stream by a source clause the fused
    // patterns have no slot for — `gdy keydown[…] z .modal jeśli …` (marker
    // BEFORE: pl z / uk з / zh 从) or `keydown[…] de .modal den eğer …`
    // (marker AFTER: ja から / ko 에서 / bn থেকে / hi से / tr den). The window
    // spans 2 pattern tokens because SOV emissions put the clause after the
    // event-marker literal, not directly after the event. See
    // tryConsumeEventSourceClause for the guards.
    let sourceWindow = 0;

    for (let i = 0; i < patternTokens.length; i++) {
      const patternToken = patternTokens[i];

      if (sourceWindow > 0 && patternToken.type === 'literal') {
        this.tryConsumeEventSourceClause(tokens, captured, patternToken);
      }

      const matched = this.matchPatternToken(
        tokens,
        patternToken,
        captured,
        patternTokens[i + 1] ?? nextAfterSequence
      );

      sourceWindow =
        patternToken.type === 'role' && patternToken.role === 'event'
          ? 2
          : Math.max(0, sourceWindow - 1);

      if (!matched) {
        // If token is optional, continue
        if (this.isOptional(patternToken)) {
          continue;
        }
        return false;
      }
    }

    return true;
  }

  /**
   * Consume a source clause (`<marker> <selector>` or `<selector> <marker>`,
   * per the profile's source-marker position) sitting between the event head
   * and the next pattern literal. Fired only inside the post-event window and
   * only when the upcoming literal does NOT match the stream position — a
   * pattern that explicitly handles the marker (`bei {event} von {source}`)
   * is left alone, and a failing consumption is harmless because matchPattern
   * resets the stream on failure.
   */
  private tryConsumeEventSourceClause(
    tokens: TokenStream,
    captured: Map<SemanticRole, SemanticValue>,
    upcoming: PatternToken & { type: 'literal' }
  ): void {
    if (captured.has('source')) return;
    const tok0 = tokens.peek();
    const tok1 = tokens.peek(1);
    if (!tok0 || !tok1) return;
    // The pattern explicitly expects this token next — leave it alone.
    if (this.patternTokenWouldMatch(upcoming, tok0)) return;

    const src = this.currentProfile?.roleMarkers?.source;
    const isMarker = (t: LanguageToken): boolean => {
      if (t.kind !== 'particle' && t.kind !== 'keyword') return false;
      if ((t.normalized ?? '').toLowerCase() === 'source') return true;
      if (!src) return false;
      const v = t.value.toLowerCase();
      if (src.primary?.toLowerCase() === v) return true;
      return !!src.alternatives?.some(a => a.toLowerCase() === v);
    };
    const position = src?.position;

    // Prepositional `from .modal` (pl z, uk з, zh 从, de von, sw kutoka).
    if (position !== 'after' && isMarker(tok0) && tok1.kind === 'selector') {
      tokens.advance();
      const v = this.tokenToSemanticValue(tokens.advance());
      if (v) captured.set('source', v);
      return;
    }
    // Postpositional `.modal から` (ja から, ko 에서, bn থেকে, hi से, tr den).
    if (position === 'after' && tok0.kind === 'selector' && isMarker(tok1)) {
      const v = this.tokenToSemanticValue(tok0);
      tokens.advance();
      tokens.advance();
      if (v) captured.set('source', v);
    }
  }

  /**
   * Match a single pattern token against the current position in the stream.
   */
  private matchPatternToken(
    tokens: TokenStream,
    patternToken: PatternToken,
    captured: Map<SemanticRole, SemanticValue>,
    nextPatternToken?: PatternToken
  ): boolean {
    switch (patternToken.type) {
      case 'literal':
        return this.matchLiteralToken(tokens, patternToken);

      case 'role':
        return this.matchRoleToken(tokens, patternToken, captured, nextPatternToken);

      case 'group':
        return this.matchGroupToken(tokens, patternToken, captured, nextPatternToken);

      default:
        return false;
    }
  }

  /**
   * Match a literal pattern token (keyword or particle).
   */
  private matchLiteralToken(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'literal' }
  ): boolean {
    const token = tokens.peek();
    if (!token) return false;

    // Check main value
    const matchType = this.getMatchType(token, patternToken.value);
    if (matchType !== 'none') {
      this.totalKeywordMatches++;
      if (matchType === 'stem') {
        this.stemMatchCount++;
      }
      tokens.advance();
      return true;
    }

    // Check alternatives
    if (patternToken.alternatives) {
      for (const alt of patternToken.alternatives) {
        const altMatchType = this.getMatchType(token, alt);
        if (altMatchType !== 'none') {
          this.totalKeywordMatches++;
          if (altMatchType === 'stem') {
            this.stemMatchCount++;
          }
          tokens.advance();
          return true;
        }
      }
    }

    // Hyphen/underscore-compound fold: many keyword translations are compounds
    // (ms `titik-henti`, es `punto-interrupción`, ru `по_умолчанию`,
    // qu `ñawpaq_kaq`) that the tokenizers shatter into word/sep/word runs —
    // and a segment can be ANOTHER command's keyword (ms `henti` → halt,
    // qu `ñawpaq` → first), so the run mis-anchors that command instead.
    // Join the run and compare it to any separator-bearing expected value;
    // only fires after every single-token match above has failed.
    for (const value of [patternToken.value, ...(patternToken.alternatives ?? [])]) {
      for (const sep of ['-', '_'] as const) {
        if (value.includes(sep) && this.tryMatchShatteredCompound(tokens, value, sep)) {
          this.totalKeywordMatches++;
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Fold a shattered hyphen-compound run into a command action for an {action}
   * role: join `word (- word)+`, look the joined form up among the current
   * profile's command keywords (primary or alternatives, case-insensitive),
   * and return the command's canonical action as a literal. Consumes the run
   * on success, leaves the stream untouched on failure.
   */
  private tryFoldHyphenActionKeyword(tokens: TokenStream): SemanticValue | null {
    const profile = this.currentProfile;
    if (!profile) return null;
    const mark = tokens.mark();
    const parts: string[] = [];
    for (;;) {
      const word = tokens.peek();
      if (!word || (word.kind !== 'identifier' && word.kind !== 'keyword')) break;
      parts.push(word.value);
      tokens.advance();
      const sep = tokens.peek();
      if (!sep || sep.value !== '-') break;
      tokens.advance();
      parts.push('-');
    }
    // A run must end on a word (parts like [w, -, w]); a trailing '-' or a
    // single word means no compound formed.
    if (parts.length < 3 || parts[parts.length - 1] === '-') {
      tokens.reset(mark);
      return null;
    }
    const joined = parts.join('').toLowerCase();
    for (const [action, kw] of Object.entries(profile.keywords)) {
      if (
        kw.primary.toLowerCase() === joined ||
        kw.alternatives?.some(a => a.toLowerCase() === joined)
      ) {
        return createLiteral(action);
      }
    }
    tokens.reset(mark);
    return null;
  }

  /**
   * Match a compound keyword (`titik-henti`, `по_умолчанию`) against a
   * shattered token run (`titik` `-` `henti` / `по` `_` `умолчанию`): word
   * segments must be identifier/keyword/particle tokens equal to the expected
   * segments (case-insensitive; particle because prepositions like ru `по` /
   * uk `за` lead some underscore compounds), separators lone `sep` tokens.
   * Consumes the run on success, resets the stream on failure.
   */
  private tryMatchShatteredCompound(
    tokens: TokenStream,
    expected: string,
    sep: '-' | '_'
  ): boolean {
    const segments = expected.split(sep);
    if (segments.length < 2 || segments.some(s => s.length === 0)) return false;
    const mark = tokens.mark();
    for (let i = 0; i < segments.length; i++) {
      if (i > 0) {
        const sepToken = tokens.peek();
        if (!sepToken || sepToken.value !== sep) {
          tokens.reset(mark);
          return false;
        }
        tokens.advance();
      }
      const word = tokens.peek();
      if (
        !word ||
        (word.kind !== 'identifier' && word.kind !== 'keyword' && word.kind !== 'particle') ||
        word.value.toLowerCase() !== segments[i]!.toLowerCase()
      ) {
        tokens.reset(mark);
        return false;
      }
      tokens.advance();
    }
    return true;
  }

  /**
   * Match a role pattern token (captures a semantic value).
   * Handles multi-token expressions like:
   * - 'my value' (possessive keyword + property)
   * - '#dialog.showModal()' (method call)
   * - "#element's *opacity" (possessive selector + property)
   */
  private matchRoleToken(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'role' },
    captured: Map<SemanticRole, SemanticValue>,
    nextPatternToken?: PatternToken
  ): boolean {
    // Skip noise words like "the" before selectors (English idiom support)
    this.skipNoiseWords(tokens);

    const token = tokens.peek();
    if (!token) {
      return patternToken.optional || false;
    }

    // Action-role hyphen-compound fold (same family as the matchLiteralToken
    // fold): a fused event pattern's {action} slot captures ONE token, but a
    // hyphen-compound verb translation (it `punto-interruzione`) shatters into
    // a word/-/word run — the slot then captures the first fragment as a junk
    // action and the whole command drops. Join the run; if it is a profile
    // keyword for a command, capture that command's canonical action instead.
    if (patternToken.role === 'action' && this.currentProfile && tokens.peek(1)?.value === '-') {
      const foldedAction = this.tryFoldHyphenActionKeyword(tokens);
      if (foldedAction) {
        captured.set(patternToken.role as SemanticRole, foldedAction);
        return true;
      }
    }

    // Event-anchor guard. An event name is a bare identifier or known event
    // keyword (`click`, `keyup`, a custom `myEvent`) — NEVER a selector, URL,
    // method call (`fn()`), or string/number literal. In an SOV reorder the
    // object is fronted ahead of the verb, so a bare-event pattern (`{event}`)
    // or `{event} <marker>` pattern would otherwise mis-anchor a fronted
    // `/api/data` / `myFunction()` as the handler event (every role matched any
    // token). Reject non-event-shaped tokens for the `event` role so the input
    // falls through to the command stage (or the per-command SOV event pattern
    // whose patient role legitimately captures it). See
    // docs-internal/HANDOFF-sov-event-anchor.md.
    if (patternToken.role === 'event' && this.currentPatternCommand === 'on') {
      if (!PatternMatcher.tokenLooksLikeEvent(token)) {
        return patternToken.optional || false;
      }
      // R1 cluster C (the surviving tail of the #508 guard): a fronted
      // POSITIONAL phrase or POSSESSIVE head is never an event either — the
      // expression assemblers below would otherwise capture it wholesale into
      // the event role (hi `पहला <input/> …` → event:expression via the
      // positional matcher, focus-patient defaults to me; hi `मेरा @data-count
      // को लोड पर डिफ़ॉल्ट …` → event:property-path via the possessive matcher,
      // the default body gets garbage roles). Rejecting here lets the input
      // fall through to the per-command pattern whose patient/destination
      // legitimately captures the fronted phrase — the same fall-through
      // contract as tokenLooksLikeEvent.
      const evNorm = (token.normalized ?? token.value).toLowerCase();
      if (PatternMatcher.POSITIONAL_OR_SCOPE_KEYWORDS.has(evNorm)) {
        return patternToken.optional || false;
      }
      if (
        this.currentProfile &&
        (getPossessiveReference(this.currentProfile, token.value) ??
          getPossessiveReference(this.currentProfile, evNorm)) !== undefined
      ) {
        return patternToken.optional || false;
      }
      // An optional-chaining head fused into one token (hi
      // `मेरा?.dataset?.customValue`) is a property access, never an event.
      if (token.value.includes('?.')) {
        return patternToken.optional || false;
      }
    }

    // A STRUCTURAL keyword (if/unless/else/end/then) is never an event payload
    // on ANY command — unlike the shape guard above, this applies beyond `on`.
    // The SOV verb-first trigger pattern (`引き金 {event}`) otherwise swallows
    // the `もし` (if) that opens the NEXT juxtaposed clause as its event
    // (`removable:before を 引き金` + `もし effect …` → event:literal="if"),
    // which both fabricates a junk event AND hides the if-keyword from the
    // conditional folds — the whole `if effect is "fade" transition …` block
    // then degrades to junk verb-anchoring (behavior-removable's transition
    // roles, SOV six). The send/trigger payload contract (string literal or
    // identifier event names, see the currentPatternCommand note) is untouched:
    // no real event is ever named `if`/`end`/`else`/`unless`/`then`. `init` is
    // deliberately NOT listed — `trigger init` is a legitimate corpus event.
    if (patternToken.role === 'event') {
      const evStructNorm = (token.normalized ?? token.value).toLowerCase();
      if (PatternMatcher.STRUCTURAL_NEVER_EVENT.has(evStructNorm)) {
        return patternToken.optional || false;
      }
    }

    // A sequence CONNECTIVE (`then`) or stray BLOCK TERMINATOR (`end`), by
    // normalized form, is never a role VALUE on any command. The trailing
    // marker-less optional {quantity} slot of the generated increment
    // patterns otherwise swallows the normalized connective that opens the
    // next statement (it `allora`, pl `wtedy`, ar `ثم` → quantity:literal=
    // "then" — increment by NaN at runtime; repeat-while/repeat-until-event
    // across it/ms/pl/ru/uk/ar/tl/vi), and the generated verb-first wait
    // duration slot swallows a following তারপর/শেষ the same way (bn
    // duration="then"/"end" ×5 — R3 families F1/F2). Kind-gated to `keyword`
    // so a string literal "then" (kind `literal`) is untouched; en
    // connectives normalize identically, so the reference parse and every
    // translation change symmetrically. Multi-token literal phrases (`at end
    // of`) match via matchLiteralToken and never reach this. Deliberately NOT
    // the full CLAUSE_BOUNDARY_KEYWORDS set: `and` collides with the
    // untranslated English pronoun `I` (pl `i` = and — the unless-condition
    // corpus), and `end` keeps its positional-`last` reading before a
    // selector (bn `শেষ <li/>`, the isBlockEndToken lookahead). An optional
    // slot skips (the schema default fills, e.g. quantity=1); a required slot
    // fails the pattern — the same net effect as the load-bearing
    // capture-and-fail (see the trailing-verb guard below).
    if (token.kind === 'keyword') {
      const connNorm = (token.normalized ?? token.value).toLowerCase();
      if (connNorm === 'then' || (connNorm === 'end' && tokens.peek(1)?.kind !== 'selector')) {
        return patternToken.optional || false;
      }
      // A CURATED end word (by VALUE — tr `son`, qu `tukuy`) is never a role
      // value, even before a selector and even when the tokenizer normalizes
      // it to something else (tr son→'last', the positional homonym): the
      // curated sets are audited so those words are always the terminator
      // (tr emits `sonuncu` for positional last). Without this, a loop's own
      // `son` directly before the next command's selector matched
      // tryMatchPositionalExpression and prefixed that command's role value
      // (tr behavior-sortable remove.patient="last .{dragClass}").
      if (isCuratedEndKeyword(token.value, this.currentProfile?.code ?? '')) {
        return patternToken.optional || false;
      }
    }

    // A `duration` slot is never a positional/scope keyword. The temporal
    // `in {duration}` idiom (`in 2s`) otherwise greedily swallows a *locative*
    // `in closest <form/>` (the scope of `focus first <input/> in closest <form/>`)
    // and spuriously emits a `wait` — which corrupted the English *reference* parse
    // (`focus first … in closest …` → {focus, wait}) and made first-in-parent /
    // focus-trap read as lossy in every other language. `closest` tokenizes as a
    // `literal` (same type as `2s`), so this keyword guard is the disambiguator.
    if (patternToken.role === 'duration') {
      const norm = (token.normalized ?? token.value).toLowerCase();
      if (PatternMatcher.POSITIONAL_OR_SCOPE_KEYWORDS.has(norm)) {
        return patternToken.optional || false;
      }
    }

    // A TRAILING optional role slot never captures a bare command VERB. halt's
    // optional patient (`halt {patient}`, patient is the pattern's last token)
    // otherwise swallows the juxtaposed next command's keyword — `… halt call
    // saveDocument()` → patient=literal:"call" — and the call drops from the en
    // reference AND every same-path language (window-keydown, en + 16 SVO
    // languages; the SOV seven split verb-first and kept it). Scoped to the
    // FINAL slot only (nextPatternToken undefined): a mid-pattern optional slot
    // must keep capturing the verb and failing the pattern — that failure is
    // load-bearing (ja `opacity を 遷移 0 に 300ms`: the no-goal variant's
    // duration/style slots capture 遷移, the pattern fails, and the richer
    // verb-anchoring fallback reclaims goal+duration; skipping instead lets the
    // sloppy pattern complete and strand the tail). Also away from
    // `event`/`action` roles, which carry their own bespoke guards above.
    if (
      patternToken.optional &&
      nextPatternToken === undefined &&
      patternToken.role !== 'event' &&
      patternToken.role !== 'action' &&
      token.kind === 'keyword'
    ) {
      const verbNorm = (token.normalized ?? token.value).toLowerCase();
      if (verbNorm in commandSchemas) {
        return true; // skip the optional slot; the verb starts the next command
      }
    }

    // Check for a positional query expression (e.g., 'last <.message/> in #chat',
    // 'first <button/> in .modal'). Triggered only when the role starts with a
    // positional keyword, so non-positional roles are unaffected.
    const positionalValue = this.tryMatchPositionalExpression(tokens);
    if (positionalValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (!isTypeCompatible(positionalValue.type, patternToken.expectedTypes)) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, positionalValue);
      return true;
    }

    // Check for a caret-scoped variable read (e.g. `^count on #host` — a
    // DOM-scoped `^name` variable read from a specific element). The `^` prefix
    // disambiguates this `on` from the event/destination `on`, so a normal
    // `toggle .active on #button` (selector patient) is never affected. Folds
    // `^name on <element>` into one expression value so a following destination
    // (`into me`) still matches.
    const caretScopeValue = this.tryMatchCaretScopeExpression(tokens);
    if (caretScopeValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (!isTypeCompatible(caretScopeValue.type, patternToken.expectedTypes)) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, caretScopeValue);
      return true;
    }

    // Multi-token OPERATOR-RUN expression (cluster A2): `"Hello, " + my value`,
    // `(the value of #price as Number) * (my value as Number)`. The tokenizers
    // split these into value/operator/value token runs, so a single-token role
    // capture takes only the first operand and the pattern then dies on the
    // operator — the en reference silently dropped the `+ my value` tail
    // (patient:literal="Hello, ") and every SOV set fell through to the
    // role-swapping verb-anchoring fallback. Assembling the whole run into one
    // expression value lets the surrounding pattern match at position 0. Strictly
    // pairwise (value OP value (OP value)*): it fires only when a binary operator
    // DIRECTLY follows the first operand, so every non-arithmetic capture is
    // untouched. Skipped for the event role (an event name is never an operand).
    if (
      patternToken.role !== 'event' &&
      (!patternToken.expectedTypes || patternToken.expectedTypes.includes('expression'))
    ) {
      // `beep! <expr>` first: its head (`beep`) is a plain value token, so the
      // single-token capture below would otherwise take it and strand the
      // `! <expr>` tail as unconsumed — the same silent truncation the operator
      // run fixes for `"Hello, " + my value`, in prefix form.
      const beepValue = this.tryMatchBeepPrefixExpression(tokens);
      if (beepValue) {
        captured.set(patternToken.role, beepValue);
        return true;
      }
      const runValue = this.tryMatchOperatorRunExpression(tokens);
      if (runValue) {
        captured.set(patternToken.role, runValue);
        return true;
      }
    }

    // Multi-token PICK RANGE expression: `0 to 5`, `start to end`,
    // `0 to 5 inclusive`. The canonical `pick (item|character)s <range> of <root>`
    // range sub-grammar is `[at|from] (start|<expr>) [(to|..) (end|<expr>)]
    // [inclusive|exclusive]`, but a single-token {patient} slot captures only the
    // first endpoint (`0`) and the pattern then dies on the ` to 5 …` tail — which
    // is exactly why the en reference of `pick characters 0 to 5 of #note`
    // truncated to `pick characters` (dropped `0 to 5 of #note`) and failed the
    // canonical parser (the lone pick-text-range canonical-validity allowlist
    // entry). Folding the whole range into one expression value (raw `0 to 5`)
    // lets the surrounding pick pattern's trailing `of {source}` still match, and
    // the value renders verbatim (renderer `valueToNaturalString` case
    // 'expression' → value.raw). The pick mapper splits the raw back into
    // rangeStart/rangeEnd/rangeMode for the core AST.
    //
    // Gated to the pick command's {patient} role: the range separator is a bare
    // `to` between two operands, which no other command's patient carries,
    // and — critically — no FOREIGN pick translation uses a literal English `to`
    // (es `a`, ru `в`, ja `を`), so the generated foreign `pick {patient} …`
    // patterns decline this fold and parse byte-identically. Arcs 2/3 widen the
    // separator to per-profile range markers.
    if (
      this.currentPatternCommand === 'pick' &&
      patternToken.role === 'patient' &&
      (!patternToken.expectedTypes || patternToken.expectedTypes.includes('expression'))
    ) {
      const rangeValue = this.tryMatchPickRangeExpression(tokens);
      if (rangeValue) {
        captured.set(patternToken.role, rangeValue);
        return true;
      }
    }

    // Check for an "of"-possessive expression (e.g. "*--primary-color of #theme",
    // AR "*--primary-color من #theme", TL "*--primary-color ng #theme"). Gated to
    // roles that opt into property-path (currently `set`'s destination), so the
    // "of"/source marker can't be confused with a real source role on other
    // commands (e.g. `get data from #input`).
    if (patternToken.expectedTypes?.includes('property-path')) {
      const ofPossessive = this.tryMatchOfPossessiveExpression(tokens);
      if (ofPossessive) {
        captured.set(patternToken.role, ofPossessive);
        return true;
      }
      // Post-nominal (property-first) possessive: `textContent لي` (ar),
      // `textContent saya` (id), `textContent yangu` (sw) — the possessor follows
      // the property in `after-object` languages. The pre-nominal matcher below
      // only handles possessor-first (`my value`), so these either failed to parse
      // (ar/id/sw) or parsed lossily, dropping the possessor (ru/pl/uk). Gated to
      // property-path roles (set's destination) so it can't misread an ordinary
      // role value. See MULTILINGUAL_BEHAVIORS_PLAN.md Phase 2 tail.
      const postNominalPossessive = this.tryMatchPostNominalPossessiveExpression(tokens);
      if (postNominalPossessive) {
        captured.set(patternToken.role, postNominalPossessive);
        return true;
      }
    }

    // Check for possessive expression (e.g., 'my value', 'its innerHTML')
    const possessiveValue = this.tryMatchPossessiveExpression(tokens);
    if (possessiveValue) {
      // Validate expected types if specified
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (
          !patternToken.expectedTypes.includes(possessiveValue.type) &&
          !patternToken.expectedTypes.includes('expression')
        ) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, possessiveValue);
      return true;
    }

    // Check for method call expression (e.g., '#dialog.showModal()')
    const methodCallValue = this.tryMatchMethodCallExpression(tokens);
    if (methodCallValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (
          !patternToken.expectedTypes.includes(methodCallValue.type) &&
          !patternToken.expectedTypes.includes('expression')
        ) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, methodCallValue);
      return true;
    }

    // Check for a bare function-call expression (e.g., `myFunction()`,
    // `adjustLayout(a, b)`). The multilingual tokenizers split this into
    // `myFunction` `(` `…` `)` (the parens tokenize as identifiers, not
    // punctuation, so `tryMatchMethodCallExpression` — which needs a
    // `selector.method()` shape — does not fold it). Verb-first SVO/VSO leaves
    // the dangling `( )` harmlessly after a captured `{patient}`, but a
    // verb-final SOV role (`{patient} <marker> … <verb>`) captures only the
    // bare name, and the dangling `( )` then break the marker match — dropping
    // the whole command (qu/bn `call myFunction()` → NULL) or letting a
    // bare-event pattern mis-anchor the name (hi). Fold the whole `name(...)`
    // into one expression value so the role captures it cleanly.
    //
    // Skipped in two cases:
    //  - the `on` HANDLER's `event` role: `on pointerdown(clientX, clientY)`
    //    destructures EVENT PARAMS, not a call — the event name (`pointerdown`)
    //    must be captured alone, with params handled by the event-handler
    //    builder (folding would corrupt the event name to
    //    `pointerdown(clientX, clientY)`). A COMMAND's event role (`send
    //    update(value: 42) to #target`, trigger likewise) is the opposite: the
    //    call shape IS the event-with-detail, and skipping the fold leaves a
    //    dangling `( )` that breaks the following destination group — en
    //    truncated the event to `literal="update"` AND dropped `to #target`
    //    (send-with-detail ×21; translations captured both and were penalized).
    //  - DECLARATION commands (`behavior`/`def`/`install`): `Draggable(dragHandle)`
    //    is a declaration SIGNATURE, not a call. Folding it lets the single-command
    //    declaration pattern (e.g. SOV `{name} কে আচরণ`) consume the first line of a
    //    multi-line `behavior … end` block and return a degenerate match, shadowing
    //    the faithful full-block parse (bn draggable/sortable/…). Leaving the
    //    dangling `(params)` makes that pattern fail, so the block falls through to
    //    the faithful path; the header's params are read separately by parseHeader.
    const skipBareCall =
      (patternToken.role === 'event' && this.currentPatternCommand === 'on') ||
      PatternMatcher.DECLARATION_COMMANDS.has(this.currentPatternCommand ?? '');
    const bareCallValue = skipBareCall ? null : this.tryMatchBareCallExpression(tokens);
    if (bareCallValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (
          !patternToken.expectedTypes.includes(bareCallValue.type) &&
          !patternToken.expectedTypes.includes('expression')
        ) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, bareCallValue);
      return true;
    }

    // Check for possessive selector expression (e.g., "#element's *opacity")
    const possessiveSelectorValue = this.tryMatchPossessiveSelectorExpression(tokens);
    if (possessiveSelectorValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        // property-path is compatible with selector, reference, and expression
        if (!isTypeCompatible(possessiveSelectorValue.type, patternToken.expectedTypes)) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, possessiveSelectorValue);
      return true;
    }

    // Check for property access expression (e.g., 'userData.name', 'it.data')
    // The condition position keeps the raw-span expression form: the en
    // reference's `if event.ctrlKey` is captured as a raw span (never routed
    // through this matcher), so flipping `<ref>.<prop>` to property-path here
    // would mismatch en for the SOV langs that DO route the condition through
    // the matcher (ja/ko/qu window-keydown). Scope the property-path emission
    // out of the condition role. (Role-NAME scoping, not expectedTypes —
    // `put.patient` and `if.condition` both omit property-path, relying on the
    // isTypeCompatible rule, so a type-list scope can't separate them.)
    const allowPropertyPath = patternToken.role !== 'condition';
    const propertyAccessValue = this.tryMatchPropertyAccessExpression(tokens, allowPropertyPath);
    if (propertyAccessValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (
          !patternToken.expectedTypes.includes(propertyAccessValue.type) &&
          !patternToken.expectedTypes.includes('expression')
        ) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, propertyAccessValue);
      return true;
    }

    // Check for selector + property expression (e.g., '#output.innerText')
    // This handles cases where the tokenizer produces two selector tokens
    const selectorPropertyValue = this.tryMatchSelectorPropertyExpression(tokens);
    if (selectorPropertyValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (!isTypeCompatible(selectorPropertyValue.type, patternToken.expectedTypes)) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, selectorPropertyValue);
      return true;
    }

    // Brace-run STYLE-OBJECT literal fold: `{ left: ${clientX - xoff}px; … }`
    // shatters into ~12+ identifier tokens (behavior-draggable's add patient —
    // en and the generated-path languages captured only the lone `{` or died
    // on it, while the SOV lax path fuses the run to ONE literal). A
    // standalone `{` identifier token is unambiguous: `.{cls}` dynamic class
    // selectors tokenize as ONE selector token, so no collision. Depth-tracked
    // for the nested `${…}` template braces; fires for roles that accept a
    // literal or declare no expectedTypes (the handcrafted add-*-full patient
    // — capturing a lone `{` is never right), and only when the run closes —
    // an unbalanced `{` backtracks.
    // An expression-ONLY slot (fetch's `with { … }` request options) takes the
    // object-literal fold instead, preserving source text so the expression parser
    // can build a real objectLiteral node. Slots that also accept a literal keep
    // the literal fold — switching them would retype every generated-path
    // `['literal','expression']` style-object capture.
    if (token.kind === 'identifier' && token.value === '{') {
      const types = patternToken.expectedTypes;
      const expressionOnly = types?.length === 1 && types[0] === 'expression';
      const braceRun = expressionOnly
        ? this.tryMatchBraceRunExpression(tokens)
        : !types?.length || types.includes('literal')
          ? this.tryMatchBraceRunLiteral(tokens)
          : null;
      if (braceRun) {
        captured.set(patternToken.role, braceRun);
        return true;
      }
    }

    // Naked named-arg fold — the brace-less sibling of the object-literal fold
    // above, for fetch's request-options slot ONLY (`fetch /x with
    // method:"POST" body:form`). Without it the expression-only slot captures
    // the first key (`method`) and the rest of the run drops as unconsumed
    // input (the Arc C fetch-options family). Role-NAME scoped like the
    // condition scoping above: `style` is the request-options role, and the
    // pair grammar (`key : value`, key code-shaped) never matches a legitimate
    // single-token style value.
    if (
      patternToken.role === 'style' &&
      patternToken.expectedTypes?.length === 1 &&
      patternToken.expectedTypes[0] === 'expression'
    ) {
      const nakedRun = foldNakedNamedArgsRaw(tokens);
      if (nakedRun) {
        captured.set(patternToken.role, { type: 'expression', raw: nakedRun } as SemanticValue);
        return true;
      }
    }

    // Try to extract a semantic value from the token
    const value = this.tokenToSemanticValue(token);
    if (!value) {
      return patternToken.optional || false;
    }

    // Validate expected types if specified
    if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
      if (!patternToken.expectedTypes.includes(value.type)) {
        // Canonical `@attr` typing: an attribute reference is ALWAYS a
        // selector (tokenToSemanticValue), regardless of which slot captures
        // it — the old slot-dependent reading (selector when the slot expected
        // one, expression when lax) made the SAME token type-diverge between
        // en's patterns and the generated event-role slots (add `@disabled`
        // en=selector / 18 langs=expression; toggle `@aria-expanded` the exact
        // opposite). Slots that relied on the identifier/expression reading
        // (bind's `@property`, expectedTypes ['reference','expression']) still
        // capture it — as the canonical selector.
        const isAttrRef =
          value.type === 'selector' && String((value as { value: unknown }).value).startsWith('@');
        const slotTakesExpr = patternToken.expectedTypes.some(
          t => t === 'expression' || t === 'reference'
        );
        if (!(isAttrRef && slotTakesExpr)) {
          return patternToken.optional || false;
        }
      }
    }

    captured.set(patternToken.role, value);
    tokens.advance();

    // Event-head tolerance: a bracket key-filter and/or a prepositional source
    // clause can trail the event token (`keydown` + `[key=="Tab"]` — the
    // tokenizer splits them — then `von .modal`). Fused event patterns
    // (`<marker> {event} <verb> …`) expect the wrapped command's verb right
    // after the event role, so without consuming these the whole fused match
    // fails and the input falls to a plain event pattern whose body re-parse
    // drops the wrapped block command (focus-trap's `if` across de/it/es/fr/pt).
    // Mirrors the bracket-filter skip already in the SOV/mid-stream extractors.
    if (patternToken.role === 'event') {
      const filterTok = tokens.peek();
      if (
        filterTok &&
        filterTok.kind === 'selector' &&
        filterTok.value.startsWith('[') &&
        'value' in value
      ) {
        // Fold the filter back onto the event value (the tokenizer split it off).
        captured.set(patternToken.role, createLiteral(`${String(value.value)}${filterTok.value}`));
        tokens.advance();
      }
      // `<source-marker> <element>` (`von .modal` / `kutoka .modal` / `from window`).
      // Skipped when the pattern itself expects the marker next (e.g. the
      // handcrafted `event-de-bei-source` carries an explicit `von {source}`).
      const srcMarker = tokens.peek();
      if (
        srcMarker &&
        (srcMarker.kind === 'particle' || srcMarker.kind === 'keyword') &&
        (srcMarker.normalized ?? '').toLowerCase() === 'source' &&
        !this.patternTokenWouldMatch(nextPatternToken, srcMarker)
      ) {
        const srcValueTok = tokens.peek(1);
        if (srcValueTok && (srcValueTok.kind === 'selector' || srcValueTok.kind === 'identifier')) {
          tokens.advance(); // the source marker
          const srcValue = this.tokenToSemanticValue(tokens.advance());
          if (srcValue && !captured.has('source')) {
            captured.set('source', srcValue);
          }
        }
      }
    }

    return true;
  }

  /**
   * Whether a pattern token (literal, or a group starting with a literal) would
   * match the given stream token. Used to keep the event-head source-clause
   * consumption from stealing a marker the pattern explicitly expects.
   */
  private patternTokenWouldMatch(pt: PatternToken | undefined, token: LanguageToken): boolean {
    if (!pt) return false;
    if (pt.type === 'literal') {
      if (this.getMatchType(token, pt.value) !== 'none') return true;
      return !!pt.alternatives?.some(a => this.getMatchType(token, a) !== 'none');
    }
    if (pt.type === 'group') {
      return this.patternTokenWouldMatch(pt.tokens?.[0], token);
    }
    return false;
  }

  // POSITIONAL_KEYWORDS moved to utils/expression-lexicon (exported): the
  // raw-expression join needs the same set, and a second copy would drift.

  /**
   * Positional + DOM-scope keywords that must never fill a `duration` slot (see the
   * guard in matchRoleToken). Superset of POSITIONAL_KEYWORDS plus the scope words
   * (`closest`, `parent`) that lead a locative `in <scope>`.
   */
  private static readonly POSITIONAL_OR_SCOPE_KEYWORDS = new Set([
    'first',
    'last',
    'next',
    'previous',
    'random',
    'closest',
    'parent',
  ]);

  /**
   * Structural block keywords that can never be an event payload on any
   * command (see the event-role guard in matchRoleToken). By NORMALIZED form,
   * so each language's rendered keyword (ja もし, tr eğer, …) is covered.
   * `init` is deliberately absent — `trigger init` is a real corpus event.
   */
  private static readonly STRUCTURAL_NEVER_EVENT = new Set(['if', 'unless', 'else', 'end', 'then']);

  /**
   * Reference bases that can lead a fused-dot property access (`it.value`,
   * `event.detail.message`, `my.innerHTML`). Command verbs are deliberately
   * excluded so `<verb> .class` (toggle .active) is never read as a property
   * path. The corpus uses the English reference forms (they pass through the
   * transformer verbatim); plain identifiers are handled separately.
   */
  private static readonly PROPERTY_ACCESS_BASES = new Set([
    'it',
    'me',
    'you',
    'my',
    'its',
    'your',
    'event',
    'result',
    'target',
    'detail',
    'body',
    'window',
    'document',
    'self',
    'this',
  ]);

  // COMMAND_ACTION_KEYWORDS moved to utils/expression-lexicon (exported) alongside
  // the positional-run recognizer that was its main reader. Aliased here so the two
  // remaining readers below (the fused-dot property guard and the ref-noun guard)
  // keep their existing call shape.
  private static readonly COMMAND_ACTION_KEYWORDS = COMMAND_ACTION_KEYWORDS;

  /**
   * Normalized clause-boundary keywords — sequence/conjunction tokens that close
   * one command and open the next. Tokenizers normalize every language's forms
   * to these (es `entonces`→then, uk `тоді`→then). Used by skipNoiseWords to tell
   * a `the <ref-noun>` patient phrase that ENDS a clause (`the evento entonces …`,
   * safe to skip the leaked article) from one whose ref-noun is followed by a
   * command verb (`the olay çağır …`, must NOT skip — the §7y regression).
   */
  private static readonly CLAUSE_BOUNDARY_KEYWORDS = new Set(['then', 'and', 'or', 'end', 'else']);

  /**
   * Try to match a positional query expression:
   *   <positional> <selector> [<in/from-marker> <source-selector>]
   * e.g. "last <.message/> in #chat", "first <button/> in .modal", "آخر <.message/> في #chat".
   *
   * Only fires when the role begins with a positional keyword, so ordinary
   * roles are untouched. The whole construct is captured as a single
   * expression value (the harness/AST treats positional queries as
   * expressions). The optional source clause consumes exactly one
   * `<marker> <selector>` pair, which is safe because positional queries are
   * terminal in their role (e.g. scroll's only role is the destination).
   */
  private tryMatchPositionalExpression(tokens: TokenStream): SemanticValue | null {
    // Recognizer lives in expression-lexicon so the raw-expression join shares it
    // — a condition and a then-branch carrying the SAME run (focus-trap authors
    // `last <button/> in .modal` in both) can no longer disagree about `in`.
    // Joins with a plain space here, which is this seam's pre-existing rule.
    const run = matchPositionalRun(tokens.tokens, tokens.position(), this.currentProfile);
    if (!run) return null;
    for (let n = 0; n < run.consumed; n++) tokens.advance();
    return { type: 'expression', raw: run.parts.map(p => p.text).join(' ') } as SemanticValue;
  }

  /**
   * Match a caret-scoped variable read: `^name on <element>`.
   *
   * `^name` is a DOM-scoped variable; `^name on #host` reads it from a specific
   * element rather than walking up from `me`. Gated to `^`-prefixed identifier
   * tokens followed by an `on`-marker and a selector — so the overloaded `on`
   * (event marker / destination marker) is never misread on ordinary commands
   * like `toggle .active on #button` (whose patient is a class selector, not a
   * caret variable). Works across languages: the `on` marker is matched by its
   * normalized form (ar `عند`, tl `kapag`, …) and selectors aren't translated.
   */
  private tryMatchCaretScopeExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token || token.kind !== 'identifier' || !token.value.startsWith('^')) {
      return null;
    }

    const onMarker = tokens.peek(1);
    const scope = tokens.peek(2);
    // The `on` marker is normalized to the `destination` role by the tokenizers
    // (it is the destination marker for toggle/add), so accept it by raw value
    // (`on`) or normalized role (`destination`/`on`). `into` normalizes
    // elsewhere, so a `put ^x into #y` is never misread as a scoped read.
    const markerForm = (onMarker?.normalized ?? '').toLowerCase();
    const isOnMarker =
      !!onMarker &&
      (onMarker.value.toLowerCase() === 'on' ||
        markerForm === 'destination' ||
        markerForm === 'on');
    if (!onMarker || !scope || scope.kind !== 'selector' || !isOnMarker) {
      return null;
    }

    tokens.advance(); // ^name
    tokens.advance(); // on-marker
    tokens.advance(); // scope selector
    return { type: 'expression', raw: `${token.value} on ${scope.value}` } as SemanticValue;
  }

  /**
   * Whether a token introduces the owner in a prepositional ("of") possessive.
   * Delegates to the shared expression lexicon, which owns the per-language
   * of-marker table so the possessive matchers and the raw-expression join
   * (`joinExpressionTokens`) cannot disagree about what an of-marker is.
   */
  private isOfPossessiveMarker(token: LanguageToken): boolean {
    return isOfPossessiveMarker(this.currentProfile, token);
  }

  /**
   * Binary operators that can join operands in an operator-run expression.
   * `*` tokenizes as a SELECTOR (the style-prefix char) but is only read as an
   * operator here when it sits BETWEEN two operands, so a bare `*opacity`
   * style selector (one fused token) is never affected.
   */
  private static readonly RUN_OPERATORS = new Set(['+', '-', '*', '/']);

  /**
   * Try to match a multi-token operator-run expression:
   *   <operand> <op> <operand> (<op> <operand>)*
   * where an operand is a parenthesized group (balanced `(`…`)` tokens), a
   * possessive pair (`my value`, `私の 値`), or a single value token.
   * Returns null — leaving the stream untouched — unless an operator DIRECTLY
   * follows the first operand, so ordinary single-token captures never change.
   */
  /**
   * Try to match canonical hyperscript's `beep! <expr>` debug prefix as ONE
   * expression value (`set $x to beep! my value`). Without this fold the value
   * slot captures the bare `beep` (a valid identifier on its own) and the
   * `! <expr>` tail drops as unconsumed — silently WRONG, and invisible to
   * every recall signal because the en REFERENCE truncates identically (the
   * probe measured 18 of 24 languages truncating; only the SOV six, which take
   * the verb-anchoring fall-through into `joinExpressionTokens`, rendered the
   * full value). See HANDOFF_foreign-validity-burndown § "beep!".
   *
   * Gates: the literal `beep` head, a SOURCE-ADJACENT `!` (the same adjacency
   * voucher the join seam's `!`-glue uses — canonical rejects the spaced
   * `beep !`, so adjacency is definitive), and a real operand following (a
   * dangling `beep!` stays a plain single-token capture). A following operator
   * run composes (`beep! my value + 1`) via the same operand consumer.
   */
  private tryMatchBeepPrefixExpression(tokens: TokenStream): SemanticValue | null {
    const head = tokens.peek();
    const bang = tokens.peek(1);
    if (!head || !bang || bang.value !== '!') return null;
    if ((head.normalized ?? head.value).toLowerCase() !== 'beep') return null;
    const adjacent =
      head.position?.end !== undefined &&
      bang.position?.start !== undefined &&
      head.position.end === bang.position.start;
    if (!adjacent) return null;

    const mark = tokens.mark();
    const startIdx = tokens.position();
    tokens.advance(); // beep
    tokens.advance(); // !
    if (!this.tryConsumeRunOperand(tokens)) {
      tokens.reset(mark);
      return null;
    }
    for (;;) {
      const op = tokens.peek();
      if (!op || !PatternMatcher.RUN_OPERATORS.has(op.value)) break;
      const beforeOp = tokens.mark();
      tokens.advance();
      if (!this.tryConsumeRunOperand(tokens)) {
        tokens.reset(beforeOp);
        break;
      }
    }
    const raw = joinExpressionTokens(
      tokens.tokens.slice(startIdx, tokens.position()),
      this.currentProfile
    );
    return { type: 'expression', raw, value: raw } as SemanticValue;
  }

  private tryMatchOperatorRunExpression(tokens: TokenStream): SemanticValue | null {
    const mark = tokens.mark();
    const startIdx = tokens.position();

    if (!this.tryConsumeRunOperand(tokens)) {
      tokens.reset(mark);
      return null;
    }

    let operands = 1;
    for (;;) {
      const op = tokens.peek();
      if (!op || !PatternMatcher.RUN_OPERATORS.has(op.value)) break;
      const beforeOp = tokens.mark();
      tokens.advance();
      if (!this.tryConsumeRunOperand(tokens)) {
        // Dangling operator — leave it (and whatever follows) unconsumed.
        tokens.reset(beforeOp);
        break;
      }
      operands++;
    }

    if (operands < 2) {
      tokens.reset(mark);
      return null;
    }

    // Join through the shared expression seam rather than a bare `.value` map:
    // the raw is read downstream as English (rendered back out, and parsed by the
    // core expression parser, which only knows English words), so a verbatim join
    // leaked the source language — es `"Hola, " + mi valor` came back byte-identical
    // instead of `"Hola, " + my value`. Also picks up the `.`-glue rule this join
    // never had, so `event.shiftKey` no longer splits into `event .shiftKey`.
    const raw = joinExpressionTokens(
      tokens.tokens.slice(startIdx, tokens.position()),
      this.currentProfile
    );
    return { type: 'expression', raw, value: raw } as SemanticValue;
  }

  /**
   * Separator that joins the two endpoints of a pick range (`0 to 5`). Matched
   * by normalized form (it tokenizes as a keyword). The canonical grammar also
   * allows `..`, but every tokenizer splits it into two bare `.` tokens, so it
   * would need a two-token separator path — deferred (not corpus-exercised; the
   * corpus and the gate row use `to`).
   */
  private static readonly PICK_RANGE_SEPARATORS = new Set(['to']);
  /**
   * Per-language pick-range separator words, matched by NATIVE VALUE (not
   * normalized form): in nearly every language the natural "0 to 5" joiner IS
   * the destination marker (es `a`, ru `в`, ja `から…` aside), so the tokenizer
   * normalizes it to `destination` — a normalized-form match would turn EVERY
   * destination marker into a range separator. Matching the value keeps the
   * table to the audited words, and the fold's gate (pick command + patient
   * role + two-operand shape, see the matchRoleToken call site) is what makes
   * even that collision safe. NEVER route this through the roleMarkers table
   * (same reason). Words are lowercased. The FIRST word of each set is
   * probe-measured from that language's corpus row (or, for the SOV six,
   * chosen in lockstep with its sovPickRangeRule render) and is exercised by
   * the 24-language table in test/pick-command.test.ts; any SECOND word is an
   * untested input-tolerance alternate (the natural "up to/until" a native
   * writer might use — de `bis`, ru/uk `до`, th `ถึง`…). Don't build on the
   * alternates without probing them first.
   * (docs-internal/HANDOFF_pick-text-range-arc3.md).
   */
  private static readonly PICK_RANGE_SEPARATORS_BY_LANG: Readonly<
    Record<string, ReadonlySet<string>>
  > = {
    ar: new Set(['إلى']),
    bn: new Set(['থেকে']),
    de: new Set(['zu', 'bis']),
    es: new Set(['a']),
    fr: new Set(['à']),
    he: new Set(['על', 'עד']),
    hi: new Set(['से']),
    id: new Set(['ke']),
    it: new Set(['in', 'a']),
    ja: new Set(['から']),
    ko: new Set(['부터']),
    ms: new Set(['ke']),
    pl: new Set(['do']),
    pt: new Set(['para', 'a']),
    qu: new Set(['kama']),
    ru: new Set(['в', 'до']),
    sw: new Set(['kwa', 'hadi']),
    th: new Set(['ใน', 'ถึง']),
    tl: new Set(['sa']),
    tr: new Set(['ile']),
    uk: new Set(['в', 'до']),
    vi: new Set(['vào', 'đến']),
    zh: new Set(['到']),
  };

  /**
   * Is this token the range separator between two pick-range endpoints?
   * English `to` matches by normalized form (it tokenizes as a keyword in
   * every language and en normalizes it to itself); the per-language native
   * words match by value — see PICK_RANGE_SEPARATORS_BY_LANG for why the two
   * lookups differ.
   */
  private isPickRangeSeparator(token: LanguageToken): boolean {
    const norm = (token.normalized ?? token.value).toLowerCase();
    if (PatternMatcher.PICK_RANGE_SEPARATORS.has(norm)) return true;
    const lang = this.currentProfile?.code;
    if (!lang) return false;
    return (
      PatternMatcher.PICK_RANGE_SEPARATORS_BY_LANG[lang]?.has(token.value.toLowerCase()) ?? false
    );
  }
  /** Trailing range-mode keywords: `pick characters 0 to 5 inclusive of #note`. */
  private static readonly PICK_RANGE_MODES = new Set(['inclusive', 'exclusive']);

  /**
   * Try to match a pick-range expression: `<start> to <end> [inclusive
   * |exclusive]`, where each endpoint is a numeric/identifier value or the range
   * keyword `start`/`end`. Returns one expression value carrying the canonical
   * surface (`0 to 5`, `0 to 5 inclusive`) so the pick pattern's trailing
   * `of {source}` still matches and the value renders verbatim. Stream is left
   * untouched (reset) on any non-match — including a lone endpoint with no
   * separator, so a foreign `pick {patient}` slot that captures a single value
   * (es `characters`) is unaffected. See the call site in matchRoleToken.
   */
  private tryMatchPickRangeExpression(tokens: TokenStream): SemanticValue | null {
    const mark = tokens.mark();
    const startIdx = tokens.position();

    if (!this.tryConsumePickRangeOperand(tokens)) {
      tokens.reset(mark);
      return null;
    }

    const sep = tokens.peek();
    if (!sep || !this.isPickRangeSeparator(sep)) {
      tokens.reset(mark);
      return null;
    }
    tokens.advance();

    if (!this.tryConsumePickRangeOperand(tokens)) {
      tokens.reset(mark);
      return null;
    }

    // Optional trailing range mode (`inclusive`/`exclusive`).
    const mode = tokens.peek();
    if (
      mode &&
      PatternMatcher.PICK_RANGE_MODES.has((mode.normalized ?? mode.value).toLowerCase())
    ) {
      tokens.advance();
    }

    // Fold-time ENGLISH normalization: synthesize `<a> to <b> [inclusive|
    // exclusive]` rather than joining the surface tokens. The folded value is
    // then canonical English for every language (es `0 a 5` → `0 to 5`), so
    // the pick mapper's hard-coded `/\s+to\s+/` split and the en render work
    // unchanged — one normalization site instead of two per-language ones.
    // Keyword endpoints/modes emit their normalized form (native `start`/`end`
    // /mode words → English); literals and identifiers emit their value, which
    // for en makes the output byte-identical to the old token join (the
    // round-trip suite in test/pick-command.test.ts holds this invariant).
    const folded = tokens.tokens.slice(startIdx, tokens.position());
    const [a, , b, m] = folded;
    const endpointText = (t: LanguageToken): string =>
      t.kind === 'keyword' ? (t.normalized ?? t.value) : t.value;
    let raw = `${endpointText(a!)} to ${endpointText(b!)}`;
    if (m) raw += ` ${(m.normalized ?? m.value).toLowerCase()}`;
    return { type: 'expression', raw, value: raw } as SemanticValue;
  }

  /**
   * Consume one endpoint of a pick range: a single literal/identifier value, or
   * the range keyword `start`/`end` (by value). Returns false (stream untouched)
   * if the upcoming token is not an endpoint — notably a bare separator or the
   * `of`/`from` source marker, which belong to the surrounding pattern.
   */
  private tryConsumePickRangeOperand(tokens: TokenStream): boolean {
    const token = tokens.peek();
    if (!token) return false;
    const norm = (token.normalized ?? token.value).toLowerCase();
    if (this.isPickRangeSeparator(token)) return false;
    const isEndpointKeyword = norm === 'start' || norm === 'end';
    if (
      token.kind === 'literal' ||
      token.kind === 'identifier' ||
      (token.kind === 'keyword' && isEndpointKeyword)
    ) {
      tokens.advance();
      return true;
    }
    return false;
  }

  /**
   * Consume one operand of an operator run. Returns false (stream untouched)
   * if the upcoming tokens do not form an operand.
   */
  private tryConsumeRunOperand(tokens: TokenStream): boolean {
    const token = tokens.peek();
    if (!token) return false;

    // Parenthesized group: `(` … matching `)` (parens tokenize as standalone
    // identifier tokens; role markers INSIDE the parens — hi `के_रूप_में`'s में —
    // are opaque to the balance count, which is the point).
    if (token.value === '(') {
      const mark = tokens.mark();
      let depth = 0;
      while (!tokens.isAtEnd()) {
        const t = tokens.advance();
        if (t.value === '(') depth++;
        else if (t.value === ')') {
          depth--;
          if (depth === 0) return true;
        }
      }
      tokens.reset(mark); // unbalanced
      return false;
    }

    // Possessive pair (`my value`, `私の 値`, `mi valor`).
    if (this.tryMatchPossessiveExpression(tokens)) return true;

    // Single value token. Particles/conjunctions/punctuation are never
    // operands (they belong to the surrounding pattern).
    if (
      (token.kind === 'literal' ||
        token.kind === 'identifier' ||
        token.kind === 'selector' ||
        token.kind === 'keyword') &&
      !PatternMatcher.RUN_OPERATORS.has(token.value) &&
      token.value !== ')'
    ) {
      tokens.advance();
      return true;
    }
    return false;
  }

  /**
   * Whether a token is a possessive pronoun in the active profile (`my`, `لي`,
   * `yangu`) rather than a property name.
   */
  private isPossessorToken(token: { value: string; normalized?: string }): boolean {
    if (!this.currentProfile) return false;
    for (const cand of [token.value, token.normalized].filter(Boolean) as string[]) {
      if (
        getPossessiveReference(this.currentProfile, cand) ??
        getPossessiveReference(this.currentProfile, cand.toLowerCase())
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Whether a bare identifier can head a possessive property phrase — `valor de
   * #picker`, `textContent von #status`.
   *
   * Sigil-led identifiers are values, not properties: `$x`/`:x` tokenize as
   * references and `@x`/`*x`/`.x`/`#x` as selectors, so `$total de #x` must never
   * fold into a property-path. Structural keywords, role-marker concepts and
   * possessive pronouns belong to other matchers and are never property names.
   */
  private isBareWordPropertyHead(token: LanguageToken): boolean {
    if (token.kind !== 'identifier') return false;
    if (/^[$:@#.*]/.test(token.value)) return false;
    if (this.isStructuralKeyword(token.value)) return false;
    if (token.normalized && this.isStructuralKeyword(token.normalized)) return false;
    if (token.normalized && this.isRoleMarkerConcept(token.normalized)) return false;
    return !this.isPossessorToken(token);
  }

  /**
   * Normalize a captured possessive property surface to its English DOM-property
   * name (`valor`/`値`/`قيمة` → `value`). Only the property HEAD is translated —
   * chained member access (`.style.display`) and method calls are code and stay
   * verbatim. An unlisted surface (an English property, a `*`/`@` selector) is
   * returned unchanged. Keeps foreign→English renders canonical and makes the
   * AST-execution path read the real DOM property. See
   * `./utils/expression-lexicon.ts`.
   */
  private toEnglishProperty(surface: string): string {
    return this.currentProfile ? translatePropertyName(this.currentProfile.code, surface) : surface;
  }

  /**
   * Try to match a prepositional "of" possessive:
   *   <property> <of-marker> <owner-selector>
   * e.g. "*--primary-color of #theme" → property-path(#theme, *--primary-color),
   * or "valor de #picker" → property-path(#picker, valor).
   *
   * This is the surface the i18n transformer emits for "set the X of #y to Z"
   * across languages (`set #y's X` would be the alternative, but the transformer
   * uses the `of` form). Only called for property-path roles (see matchRoleToken).
   */
  private tryMatchOfPossessiveExpression(tokens: TokenStream): SemanticValue | null {
    const property = tokens.peek();
    if (!property) return null;
    // A selector head is the historical form (`*--primary-color of #theme`). A
    // bare-word head is the property-first render the transformer emits for
    // `#picker's value` in es/pt/fr/de/id/sw/ar (`valor de #picker`), which
    // otherwise captured the property word alone as an expression and dropped
    // the owner. Selector-first languages (en `#picker's value`, ja `#pickerの
    // 値`) never reach here: their owner check below rejects the property word.
    if (property.kind !== 'selector' && !this.isBareWordPropertyHead(property)) return null;

    const mark = tokens.mark();
    tokens.advance();

    const marker = tokens.peek();
    if (!marker || !this.isOfPossessiveMarker(marker)) {
      tokens.reset(mark);
      return null;
    }
    tokens.advance();

    const owner = tokens.peek();
    if (!owner || owner.kind !== 'selector') {
      tokens.reset(mark);
      return null;
    }
    tokens.advance();

    // "X of #y" means the X property of #y → property-path(object: #y, property: X).
    return createPropertyPath(createSelector(owner.value), this.toEnglishProperty(property.value));
  }

  /**
   * Try to match a possessive expression like 'my value' or 'its innerHTML'.
   * Returns the PropertyPathValue if matched, or null if not.
   */
  private tryMatchPossessiveExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token) return null;

    // Use profile-based possessive keyword lookup
    if (!this.currentProfile) return null;

    // Look up the possessive keyword by BOTH the native value and the normalized
    // form. Profiles key `possessive.keywords` by the NATIVE word (EN 'my',
    // AR 'لي', TL 'aking', HE 'שלי', KO '내'), but most non-English tokenizers
    // normalize that word to its English base ('لي' → normalized 'me'). Looking
    // up only the normalized form misses every native-keyed profile, so the
    // possessive value-filler ('my value', 'لي قيمة') never matches outside
    // English. Trying the native value first makes the lookup language-agnostic.
    const candidates = [token.value, token.normalized].filter(Boolean) as string[];
    let baseRef: string | undefined;
    for (const candidate of candidates) {
      baseRef =
        getPossessiveReference(this.currentProfile, candidate) ??
        getPossessiveReference(this.currentProfile, candidate.toLowerCase());
      if (baseRef) break;
    }

    if (!baseRef) return null;

    // We have a possessive keyword, look ahead for property name
    const mark = tokens.mark();
    tokens.advance();

    // Skip a possessive CONNECTOR for multi-word possessor-first constructions
    // (Indonesian `saya punya *background` = "I have *background" = "my
    // *background"). The connector sits between the possessor keyword and the
    // property; without skipping it, `punya` is read as the property and the
    // whole set body fails to parse (id set-style/set-text).
    const connectors = this.currentProfile.possessive?.connectors;
    if (connectors && connectors.length > 0) {
      const next = tokens.peek();
      if (next) {
        const nv = next.value.toLowerCase();
        const nn = (next.normalized ?? '').toLowerCase();
        if (connectors.some(c => c.toLowerCase() === nv || c.toLowerCase() === nn)) {
          tokens.advance();
        }
      }
    }

    const propertyToken = tokens.peek();
    if (!propertyToken) {
      // Just the possessive keyword, no property - revert
      tokens.reset(mark);
      return null;
    }

    // Property should be an identifier, keyword (not structural), or selector (for style/dot/attr)
    // Examples: "my value", "my innerHTML", "my *background", "my *opacity", "my @data-count"
    // Also handles dot-property access: "my.textContent" tokenized as "my" + ".textContent"
    // Structural keywords are checked by NORMALIZED form too — non-English
    // structural words (ms kemudian→then, tamat→end) otherwise read as a
    // property ("its then"), forming a phantom property-path whose type check
    // fails the whole pattern (ms `letak 'X' ke ia kemudian …` dropped the put).
    //
    // The same trap exists for ROLE-MARKER keywords whose normalized form is a
    // role *concept* rather than an English preposition surface word: ms `ke`
    // (into) normalizes to `destination`, `dari` (from) to `source`, `pada` (to)
    // to `destination`. `isStructuralKeyword` only knows the English surface
    // prepositions (`into`/`to`/`from`), so a possessive pronoun directly
    // followed by such a marker — `letak ia ke #container` (put it into X),
    // `letak saya ke …` (put me into …) — read `ia ke` as the phantom possessive
    // `it.ke`, the literal `ke` match then failed, and the whole put dropped
    // (the §10 "ms put-with-`ia`" bug). A marker concept is never a real DOM
    // property name, so reject it as a possessive property head.
    //
    // A positional `at end of` connective (ms `di`/`tamat`/`daripada`, zh
    // `在`/`结束`/`的`) tokenizes as a bare identifier with NO normalized concept,
    // so the guards above miss it. `letak ia di tamat daripada badan` (put it at
    // end of body, make-toast's attaching put) read `ia di` as the phantom
    // possessive `it.di`, the literal `di` then failed and the put dropped. Bail
    // so `ia` stays a bare patient and the at-end pattern matches.
    if (isAtEndConnective(this.currentProfile.code, propertyToken.value)) {
      tokens.reset(mark);
      return null;
    }
    if (
      propertyToken.kind === 'identifier' ||
      (propertyToken.kind === 'keyword' &&
        !this.isStructuralKeyword(propertyToken.value) &&
        !(propertyToken.normalized && this.isStructuralKeyword(propertyToken.normalized)) &&
        !(propertyToken.normalized && this.isRoleMarkerConcept(propertyToken.normalized)) &&
        // A COMMAND VERB is never a property name either: ms `… ke ia ukur y`
        // ("… to it" + "measure y") read `ia ukur` as the phantom possessive
        // `it.measure`, swallowing the next clause's verb — set-ms-full then
        // captured a null property-path patient and the measure vanished
        // (behavior-draggable/resizable ms, the corpus's one remaining
        // multiset deficit). Normalized-form-gated, so en (whose keywords
        // carry no normalized) keeps its reference behavior byte-identical.
        !(
          propertyToken.normalized &&
          PatternMatcher.COMMAND_ACTION_KEYWORDS.has(propertyToken.normalized.toLowerCase())
        )) ||
      (propertyToken.kind === 'selector' && propertyToken.value.startsWith('*')) ||
      (propertyToken.kind === 'selector' && propertyToken.value.startsWith('@')) ||
      (propertyToken.kind === 'selector' &&
        propertyToken.value.startsWith('.') &&
        /^\.[a-zA-Z_]\w*/.test(propertyToken.value))
    ) {
      tokens.advance();

      // For dot-property selectors (.textContent), strip the leading dot
      let propertyName = propertyToken.value;
      if (
        propertyToken.kind === 'selector' &&
        propertyName.startsWith('.') &&
        /^\.[a-zA-Z_]\w*/.test(propertyName)
      ) {
        propertyName = propertyName.substring(1);
      }

      // Normalize the property HEAD to English (`valor`/`値` → `value`) before any
      // chained member access is folded on. `my valor` renders `my value`; `my
      // valor.length` renders `my value.length` (the chained `.length` is code).
      propertyName = this.toEnglishProperty(propertyName);

      // Consume chained dot-property access (.parentElement.style.display),
      // including optional-chaining links: the tokenizer splits `?.prop` into
      // `?` + `.prop`, and stopping at the `?` left the chain HALF-consumed —
      // property-path(me, "?.dataset") with `?.customValue` stranded, so the
      // verb-final SOV patterns could never complete through to their patient
      // particle and the row fell to a bare `?.customValue` expression
      // (optional-chaining-possessive, R1 Family D). A `?` is only folded
      // when a `.prop` selector immediately follows, so a ternary after a
      // possessive value is untouched.
      let chainedProps = propertyName;
      for (;;) {
        const nxt = tokens.peek();
        if (
          nxt?.kind === 'selector' &&
          nxt.value.startsWith('.') &&
          /^\.[a-zA-Z_]\w*/.test(nxt.value)
        ) {
          chainedProps += nxt.value; // keep the dots for chaining
          tokens.advance();
          continue;
        }
        if (nxt?.value === '?') {
          const after = tokens.peek(1);
          if (
            after?.kind === 'selector' &&
            after.value.startsWith('.') &&
            /^\.[a-zA-Z_]\w*/.test(after.value)
          ) {
            chainedProps += `?${after.value}`;
            tokens.advance();
            tokens.advance();
            continue;
          }
        }
        break;
      }

      // Check for a trailing method call: chain + '(' [args...] ')'
      // (e.g., my.value.toUpperCase(), my.getAttribute("data-id")). The tokenizer
      // may emit the call as one token (`("data-id")`) or split it into `(` / args
      // / `)` (kinds vary: identifier/punctuation/literal), so consume by value
      // until the parentheses balance rather than relying on a single-token form.
      const afterChain = tokens.peek();
      if (afterChain && afterChain.value.startsWith('(')) {
        let call = '';
        let depth = 0;
        let guard = 0;
        while (!tokens.isAtEnd() && guard++ < PatternMatcher.MAX_METHOD_ARGS + 2) {
          const t = tokens.peek();
          if (!t) break;
          call += t.value;
          tokens.advance();
          for (const ch of t.value) {
            if (ch === '(') depth++;
            else if (ch === ')') depth--;
          }
          if (depth <= 0) break;
        }
        chainedProps += call;
      }

      // Create property-path: my value -> { object: me, property: 'value' }
      // baseRef from getPossessiveReference is always a valid reference ('me', 'you', 'it', etc.)
      return createPropertyPath(createReference(baseRef as ReferenceValue['value']), chainedProps);
    }

    // Not a valid property, revert
    tokens.reset(mark);
    return null;
  }

  /**
   * Match a POST-NOMINAL possessive expression: `<property> <possessor>`.
   *
   * The pre-nominal {@link tryMatchPossessiveExpression} handles possessor-first
   * order (`my value`, `لي قيمة`). Languages whose possessive `markerPosition` is
   * `after-object` put the possessor AFTER the property (ar `textContent لي`,
   * id `textContent saya`, sw `textContent yangu`), so the pre-nominal matcher
   * never fires — `set my textContent to "x"` failed to parse (ar/id/sw) or parsed
   * lossily with the possessor dropped (ru/pl/uk). This recovers the property-path
   * for that order. Gated to `after-object` profiles, so possessor-first languages
   * are untouched.
   */
  private tryMatchPostNominalPossessiveExpression(tokens: TokenStream): SemanticValue | null {
    if (!this.currentProfile) return null;
    if (this.currentProfile.possessive?.markerPosition !== 'after-object') return null;

    const propertyToken = tokens.peek();
    if (!propertyToken) return null;

    // The head must be a plausible property name — a bare identifier or a dot /
    // `*` / `@` property selector — and must not itself be a possessor, structural
    // keyword, or role-marker concept (those are handled elsewhere / are not
    // properties).
    const isDotSelector =
      propertyToken.kind === 'selector' &&
      propertyToken.value.startsWith('.') &&
      /^\.[a-zA-Z_]\w*/.test(propertyToken.value);
    const isPropHead =
      (propertyToken.kind === 'identifier' &&
        !this.isStructuralKeyword(propertyToken.value) &&
        !(propertyToken.normalized && this.isStructuralKeyword(propertyToken.normalized)) &&
        !(propertyToken.normalized && this.isRoleMarkerConcept(propertyToken.normalized))) ||
      (propertyToken.kind === 'selector' &&
        (propertyToken.value.startsWith('*') || propertyToken.value.startsWith('@'))) ||
      isDotSelector;
    if (!isPropHead || this.isPossessorToken(propertyToken)) return null;

    const mark = tokens.mark();

    let propertyName = propertyToken.value;
    if (isDotSelector) propertyName = propertyName.substring(1);
    tokens.advance();

    // Normalize the property HEAD to English before folding chained members.
    propertyName = this.toEnglishProperty(propertyName);

    // Consume chained dot-property access (`.style.display`) before the possessor.
    let chainedProps = propertyName;
    while (
      tokens.peek()?.kind === 'selector' &&
      tokens.peek()!.value.startsWith('.') &&
      /^\.[a-zA-Z_]\w*/.test(tokens.peek()!.value)
    ) {
      chainedProps += tokens.peek()!.value;
      tokens.advance();
    }

    // Now require the possessor keyword.
    const possessorToken = tokens.peek();
    if (!possessorToken) {
      tokens.reset(mark);
      return null;
    }
    let baseRef: string | undefined;
    for (const cand of [possessorToken.value, possessorToken.normalized].filter(
      Boolean
    ) as string[]) {
      baseRef =
        getPossessiveReference(this.currentProfile, cand) ??
        getPossessiveReference(this.currentProfile, cand.toLowerCase());
      if (baseRef) break;
    }
    if (!baseRef) {
      tokens.reset(mark);
      return null;
    }
    tokens.advance();

    return createPropertyPath(createReference(baseRef as ReferenceValue['value']), chainedProps);
  }

  /**
   * Check if a keyword is a structural keyword (preposition, control flow, etc.)
   * that shouldn't be consumed as a property name.
   */
  private isStructuralKeyword(value: string): boolean {
    const structural = new Set([
      // Prepositions
      'into',
      'in',
      'to',
      'from',
      'at',
      'by',
      'with',
      'without',
      'before',
      'after',
      'of',
      'as',
      'on',
      // Control flow
      'then',
      'end',
      'else',
      'if',
      'repeat',
      'while',
      'for',
      // Commands (shouldn't be property names)
      'toggle',
      'add',
      'remove',
      'put',
      'set',
      'show',
      'hide',
      'increment',
      'decrement',
      'send',
      'trigger',
      'call',
    ]);
    return structural.has(value.toLowerCase());
  }

  /**
   * Whether a NORMALIZED token form is a role-marker *concept* (the abstract
   * role name a non-English marker keyword normalizes to, e.g. ms `ke`→
   * `destination`, `dari`→`source`). These are markers, never DOM property
   * names — so a possessive property head normalizing to one is a mis-read
   * (`letak ia ke …` = "put it into …", not "put it's `ke`"). Only checked
   * against the `normalized` form: a real English property surface word
   * (`value`, `style`) tokenizes as a bare identifier with no normalized concept,
   * so this never blocks a genuine `my value` / `its style` possessive.
   */
  private isRoleMarkerConcept(normalized: string): boolean {
    const markerConcepts = new Set([
      'destination',
      'source',
      'patient',
      'object',
      'event',
      'eventmarker',
      'manner',
      'instrument',
    ]);
    return markerConcepts.has(normalized.toLowerCase());
  }

  /**
   * Does `token` look like an EVENT name for the `event` role? Events are bare
   * identifiers / known event keywords (click, keyup, custom `myEvent`), never
   * selectors, URLs, method calls (`fn()`), or string/number literals.
   * Value-based so it works for any language's native event words (no special
   * chars). See the event-anchor guard in matchRoleToken and
   * docs-internal/HANDOFF-sov-event-anchor.md.
   */
  private static tokenLooksLikeEvent(token: LanguageToken): boolean {
    // Selectors (.class/#id/<tag/>/[attr]) and URLs are never events.
    if (token.kind === 'selector' || (token.kind as string) === 'url') return false;
    // Quoted strings / numbers tokenize as `literal`; events never do.
    if (token.kind === 'literal') return false;
    const v = token.value;
    // URL/path (`/api/data`) or selector/attr punctuation (`#id`, `*prop`, `@attr`):
    // never an event head. The KEY case is the fronted fetch URL — `fetch /api/data`
    // → SOV `/api/data को लाएं`, where the bare-event pattern (priority 80, Stage 1)
    // would otherwise anchor `/api/data` as the event before the command-stage
    // fetch pattern (Stage 2) runs. Rejecting it lets the fetch command pattern win.
    // Parens are deliberately NOT rejected: the `when (<expr>) changes` reactive
    // patterns capture a watched EXPRESSION in the `event` role whose first token is
    // `(` (when-value-changes), and a fronted `myFunction()` mis-anchor is already
    // outranked by the per-command patient-first pattern (priority 145 > 80).
    if (/[/#@*]/.test(v)) return false;
    if (v.startsWith('"') || v.startsWith("'") || v.startsWith('`')) return false;
    // A body-bearing block keyword fronting the input (`live`/`socket`/… → SOV
    // `लाइव …`) is a command, not an event: the bare-event pattern (priority 80,
    // Stage 1) would otherwise anchor it as the handler event before the command
    // stage's bareKeyword pattern (Stage 2) runs, dropping the block action (hi
    // live-derived-value/live-multiple-deps parsed as a bare `on` + `put`).
    // Rejecting it lets the `live`/`socket` command pattern win at Stage 2.
    const norm = token.normalized?.toLowerCase();
    if (norm && BAREKEYWORD_BLOCK_ACTIONS.has(norm)) return false;
    return true;
  }

  /**
   * Fold a depth-balanced `{ … }` token run into ONE literal value (see the
   * call site in matchRoleToken). The tokenizers emit `{`, `}`, `:`, `;`, `$`
   * as bare identifier tokens, so a CSS style-object argument is otherwise
   * unmatchable by a single-token role. Depth tracking covers the nested
   * `${expr}` template braces. Returns null (with the stream reset) when the
   * run never closes or exceeds the token cap.
   */
  private static readonly MAX_BRACE_RUN_TOKENS = 64;

  private tryMatchBraceRunLiteral(tokens: TokenStream): SemanticValue | null {
    const open = tokens.peek();
    if (!open || open.kind !== 'identifier' || open.value !== '{') return null;

    const mark = tokens.mark();
    tokens.advance(); // {
    const parts: string[] = ['{'];
    let depth = 1;
    let guard = 0;
    while (!tokens.isAtEnd() && guard++ < PatternMatcher.MAX_BRACE_RUN_TOKENS) {
      const t = tokens.advance();
      if (!t) break;
      if (t.value === '{') depth++;
      else if (t.value === '}') {
        depth--;
        if (depth === 0) {
          parts.push('}');
          return createLiteral(parts.join(' '));
        }
      }
      parts.push(t.value);
    }

    tokens.reset(mark);
    return null;
  }

  /**
   * Fold a depth-balanced `{ … }` token run into ONE **expression** value whose
   * `raw` is the original source text — the object-literal sibling of
   * tryMatchBraceRunLiteral, for roles that expect an expression (`fetch`'s
   * request-options `style` role: `fetch /x with { method: 'POST' }`).
   *
   * Unlike the literal fold, this cannot space-join token values: `raw` is handed
   * to the expression parser, and a template-literal option
   * (``{ headers: {Authorization: `Bearer ${token}`} }``) shatters into many
   * tokens whose rejoined spacing must match the source or the interpolation
   * breaks. Tokens carry source offsets, so inter-token gaps are reproduced
   * exactly. Returns null (stream reset) when the run never closes.
   */
  private tryMatchBraceRunExpression(tokens: TokenStream): SemanticValue | null {
    const open = tokens.peek();
    if (!open || open.kind !== 'identifier' || open.value !== '{') return null;

    const mark = tokens.mark();
    let raw = '';
    let prevEnd = -1;
    let depth = 0;
    let guard = 0;
    while (!tokens.isAtEnd() && guard++ < PatternMatcher.MAX_BRACE_RUN_TOKENS) {
      const t = tokens.advance();
      if (!t) break;
      if (prevEnd >= 0 && t.position.start > prevEnd) {
        raw += ' '.repeat(t.position.start - prevEnd);
      }
      raw += t.value;
      prevEnd = t.position.end;

      if (t.value === '{') depth++;
      else if (t.value === '}') {
        depth--;
        if (depth === 0) return { type: 'expression', raw } as SemanticValue;
      }
    }

    tokens.reset(mark);
    return null;
  }

  /**
   * Try to match a bare function-call expression like `myFunction()` or
   * `adjustLayout(a, b)`. The multilingual tokenizers split this into
   * `name` `(` [args] `)` with the parens as `identifier`-kind tokens (not
   * `punctuation`, which `tryMatchMethodCallExpression` requires). Folds the
   * whole call into one `expression` value so an SOV role (`{patient} <marker>`)
   * captures `name(...)` instead of just `name`, leaving the marker to match.
   * Returns null (no stream movement) unless a balanced `name( … )` is present.
   */
  private tryMatchBareCallExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token || token.kind !== 'identifier') return null;
    // Must look like a function name (rejects selectors/urls/`:local`/`$global`).
    if (!/^[A-Za-z_][\w$]*$/.test(token.value)) return null;
    const next = tokens.peek(1);
    if (!next || next.value !== '(') return null;

    const mark = tokens.mark();
    tokens.advance(); // function name
    const openParen = tokens.advance(); // (

    // Reconstruct the parenthesized content VERBATIM by source adjacency rather
    // than collecting tokens and re-joining with `, `. The tokenizer splits the
    // call into separate tokens (`value` `:` `42`), and the old comma-rejoin
    // assumed a positional arg list — so a named-argument / detail call
    // `update(value: 42)` came out as `update(value, :, 42)` (the `:` became a
    // phantom arg). Adjacency-joining preserves every shape: `a, b` stays `a, b`,
    // `value: 42` stays `value: 42` (a token glues to the previous when it abuts
    // it in the source, else a single space separates them).
    let content = '';
    let prevEnd = openParen.position?.end;
    let closed = false;
    let guard = 0;
    while (!tokens.isAtEnd() && guard++ < PatternMatcher.MAX_METHOD_ARGS + 2) {
      const argToken = tokens.peek();
      if (!argToken) break;
      if (argToken.value === ')') {
        tokens.advance(); // consume )
        closed = true;
        break;
      }
      const adjacent = prevEnd !== undefined && argToken.position?.start === prevEnd;
      content += (content === '' || adjacent ? '' : ' ') + argToken.value;
      prevEnd = argToken.position?.end;
      tokens.advance();
    }

    if (!closed) {
      tokens.reset(mark);
      return null;
    }

    return { type: 'expression', raw: `${token.value}(${content})` } as SemanticValue;
  }

  /**
   * Try to match a method call expression like '#dialog.showModal()'.
   * Pattern: selector + '.' + identifier + '(' + [args] + ')'
   * Returns an expression value if matched, or null if not.
   */
  private tryMatchMethodCallExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token || token.kind !== 'selector') return null;

    // Look ahead for: . identifier (
    const mark = tokens.mark();
    tokens.advance(); // consume selector

    const dotToken = tokens.peek();
    if (!dotToken || dotToken.kind !== 'operator' || dotToken.value !== '.') {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume .

    const methodToken = tokens.peek();
    if (!methodToken || methodToken.kind !== 'identifier') {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume method name

    const openParen = tokens.peek();
    if (!openParen || openParen.kind !== 'punctuation' || openParen.value !== '(') {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume (

    // Consume arguments until we find ) (with depth limit for security)
    const args: string[] = [];
    while (!tokens.isAtEnd() && args.length < PatternMatcher.MAX_METHOD_ARGS) {
      const argToken = tokens.peek();
      if (!argToken) break;
      if (argToken.kind === 'punctuation' && argToken.value === ')') {
        tokens.advance(); // consume )
        break;
      }
      // Skip commas
      if (argToken.kind === 'punctuation' && argToken.value === ',') {
        tokens.advance();
        continue;
      }
      // Collect arg value
      args.push(argToken.value);
      tokens.advance();
    }

    // Create expression value: #dialog.showModal()
    const methodCall = `${token.value}.${methodToken.value}(${args.join(', ')})`;
    return {
      type: 'expression',
      raw: methodCall,
    } as SemanticValue;
  }

  /**
   * Try to match a property access expression like 'userData.name' or 'it.data'.
   * Pattern: (identifier | keyword) + '.' + identifier [+ '.' + identifier ...]
   * Returns an expression value if matched, or null if not.
   */
  private tryMatchPropertyAccessExpression(
    tokens: TokenStream,
    allowPropertyPath = true
  ): SemanticValue | null {
    const token = tokens.peek();
    if (!token) return null;

    // Must start with an identifier or keyword reference
    if (token.kind !== 'identifier' && token.kind !== 'keyword') return null;

    // Look ahead for: . identifier
    const mark = tokens.mark();
    tokens.advance(); // consume first token

    // Fused-dot form: the tokenizer emits `it.value` / `event.detail.message` as
    // a base token + `.`-prefixed *selector* tokens (`.value`, `.detail`), since
    // `.foo` looks like a class selector. Fold a chain of those into the property
    // path (the `.` operator path below handles the rare un-fused form).
    //
    // Gated to identifiers and known reference bases so a command verb followed
    // by a class selector (`بدل .active` = "toggle .active") is never swallowed as
    // a property access — command verbs are never reference bases.
    const baseLower = token.value.toLowerCase();
    const fusedFirst = tokens.peek();
    if (
      (token.kind === 'identifier' || PatternMatcher.PROPERTY_ACCESS_BASES.has(baseLower)) &&
      fusedFirst &&
      fusedFirst.kind === 'selector' &&
      /^\.[a-zA-Z_]/.test(fusedFirst.value)
    ) {
      let fusedChain = token.value;
      const fusedProps: string[] = [];
      let fusedDepth = 0;
      while (fusedDepth < PatternMatcher.MAX_PROPERTY_DEPTH) {
        const prop = tokens.peek();
        if (prop && prop.kind === 'selector' && /^\.[a-zA-Z_]/.test(prop.value)) {
          fusedChain += `.${prop.value.slice(1)}`;
          fusedProps.push(prop.value.slice(1));
          tokens.advance();
          fusedDepth++;
        } else {
          break;
        }
      }
      // A fused `<ref>.<prop>` access where the base is a real reference
      // (`it.error`, `result.name`, `event.ctrlKey`) is semantically a property
      // access — emit property-path so it aligns with the translations that
      // already render the possessive as property-path (the put/set.patient R1
      // arc). Guard against a trailing method call (`target.closest("li")`): the
      // fused path consumes the `.`-selector props but a following `(` means it's
      // a method call, which must stay an expression (behavior-sortable).
      const fusedTrailing = tokens.peek();
      const fusedIsMethodCall = !!fusedTrailing && fusedTrailing.value.startsWith('(');
      if (
        allowPropertyPath &&
        !fusedIsMethodCall &&
        fusedProps.length > 0 &&
        isValidReference(baseLower)
      ) {
        return createPropertyPath(createReference(baseLower), fusedProps.join('.'));
      }
      if (fusedIsMethodCall) {
        // Consume the balanced call parens into the expression. Left in the
        // stream they broke the surrounding SOV pattern — the marker expected
        // after the role slot (`… に 設定`) met `(` instead, the whole set
        // pattern died, and the command fell to the role-swapping
        // verb-anchoring fallback (behavior-sortable `set item to the
        // target.closest("li")`, session-5 residue). The paren tokens are
        // plain identifiers in the multilingual tokenizers, so this balances
        // by VALUE, mirroring tryConsumeRunOperand's group logic.
        const callMark = tokens.mark();
        const callParts: string[] = [];
        let parenDepth = 0;
        let closed = false;
        while (!tokens.isAtEnd()) {
          const t = tokens.peek();
          if (!t) break;
          if (t.value === '(') parenDepth++;
          else if (t.value === ')') {
            callParts.push(t.value);
            tokens.advance();
            parenDepth--;
            if (parenDepth === 0) {
              closed = true;
            }
            if (closed) break;
            continue;
          }
          callParts.push(t.value);
          tokens.advance();
        }
        if (closed) {
          fusedChain += callParts.join('');
        } else {
          tokens.reset(callMark); // unbalanced — leave the parens unconsumed
        }
      }
      return { type: 'expression', raw: fusedChain } as SemanticValue;
    }

    const dotToken = tokens.peek();
    if (!dotToken || dotToken.kind !== 'operator' || dotToken.value !== '.') {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume .

    const propertyToken = tokens.peek();
    if (!propertyToken || propertyToken.kind !== 'identifier') {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume property name

    // Build the property chain
    let chain = `${token.value}.${propertyToken.value}`;
    let depth = 1; // Already have one property access

    // Continue for nested property access (e.g., userData.address.city)
    // With depth limit for security
    while (!tokens.isAtEnd() && depth < PatternMatcher.MAX_PROPERTY_DEPTH) {
      const nextDot = tokens.peek();
      if (!nextDot || nextDot.kind !== 'operator' || nextDot.value !== '.') {
        break;
      }
      tokens.advance(); // consume .

      const nextProp = tokens.peek();
      if (!nextProp || nextProp.kind !== 'identifier') {
        // Dot without property - put the dot back and stop
        // Can't easily put a single token back, so we'll include it
        break;
      }
      tokens.advance(); // consume property
      chain += `.${nextProp.value}`;
      depth++;
    }

    // Check for method call: chain + '(' + args + ')'
    // e.g., me.insertBefore(draggedItem, dropTarget)
    const openParen = tokens.peek();
    if (openParen && openParen.kind === 'punctuation' && openParen.value === '(') {
      tokens.advance(); // consume (

      // Collect arguments (comma-separated values)
      const args: string[] = [];
      let argDepth = 0; // Track nested parentheses
      while (!tokens.isAtEnd() && args.length < PatternMatcher.MAX_METHOD_ARGS) {
        const argToken = tokens.peek();
        if (!argToken) break;

        // Handle close paren - respecting nesting
        if (argToken.kind === 'punctuation' && argToken.value === ')') {
          if (argDepth === 0) {
            tokens.advance(); // consume )
            break;
          }
          argDepth--;
        }
        // Track nested open parens
        if (argToken.kind === 'punctuation' && argToken.value === '(') {
          argDepth++;
        }
        // Skip commas between arguments
        if (argToken.kind === 'punctuation' && argToken.value === ',') {
          tokens.advance();
          continue;
        }
        // Collect arg value
        args.push(argToken.value);
        tokens.advance();
      }

      // Create expression value with method call: me.insertBefore(a, b)
      const methodCall = `${chain}(${args.join(', ')})`;
      return {
        type: 'expression',
        raw: methodCall,
      } as SemanticValue;
    }

    // Un-fused `<ref> . <prop>` where the base is a real reference →
    // property-path (mirrors the fused-dot path above). Plain identifiers
    // (`userData.name`) keep the expression form. The method-call shape is
    // already handled above (the `(` branch returns an expression before here).
    const opBaseLower = token.value.toLowerCase();
    if (allowPropertyPath && isValidReference(opBaseLower)) {
      const opProps = chain.split('.').slice(1).join('.');
      return createPropertyPath(createReference(opBaseLower), opProps);
    }
    // Create expression value: userData.name
    return {
      type: 'expression',
      raw: chain,
    } as SemanticValue;
  }

  /**
   * Try to match a possessive selector expression like "#element's *opacity".
   * Pattern: selector + possessive-marker + (selector | identifier)
   *
   * The possessive marker is English `'s` (a punctuation token) or, for other
   * languages, the active profile's `possessive.marker` emitted as a `particle`
   * token (e.g. Japanese `の`, Korean `의`). Returns a property-path value if
   * matched, or null if not.
   */
  private tryMatchPossessiveSelectorExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token || token.kind !== 'selector') return null;

    // Look ahead for the possessive marker.
    const mark = tokens.mark();
    tokens.advance(); // consume selector

    const possessiveToken = tokens.peek();
    // Agglutinative suffix markers may be written with a leading hyphen in the
    // profile (e.g. Quechua `-pa`) but tokenize without it; normalize before
    // comparing.
    const profileMarker = this.currentProfile?.possessive?.marker?.replace(/^-/, '');
    const isEnglishPossessive =
      !!possessiveToken && possessiveToken.kind === 'punctuation' && possessiveToken.value === "'s";
    // The en tokenizer splits the possessive clitic `'s` into two tokens (`'` + `s`)
    // when it trails a selector — `#picker's value` → `#picker ' s value` — so the
    // single-token check above misses it and the property (`value`/`textContent`) is
    // dropped, capturing only the bare `#picker` selector. ja/ko/… keep their の/의
    // possessive whole, so they captured the full property-path while en lost it (the
    // bind-explicit-property / bind-non-form-display R1 cap). Detect the split pair.
    const splitEnglishPossessive =
      !!possessiveToken &&
      possessiveToken.value === "'" &&
      (tokens.peek(1)?.value ?? '').toLowerCase() === 's';
    // Non-English markers (の, 의, র, pa, …) arrive as `particle` tokens. Match
    // them against the active profile's possessive marker. Empty markers
    // (Turkish, suffix-fused) don't have a standalone token and aren't handled here.
    //
    // Marker-role collision gate: a Romance genitive connector doubles as a
    // role marker (es/pt `de` is BOTH the possessive connector AND remove's
    // from-marker, normalized `source`). When the marker normalizes to `source`
    // and the CURRENT command's schema declares a source role, the marker
    // belongs to the command — folding it as a possessive steals the pattern's
    // own marker (`quitar .{dragClass} de item` read as ".{dragClass}'s item" →
    // phantom property-path patient, source lost to the me-default). `set`
    // declares no `source`, so set-color-variable's genitive fold is untouched.
    // Deliberately `source`-ONLY: the SOV genitives qu `pa` / tr `nin`
    // normalize to `destination`, and their possessive folds are load-bearing
    // on commands that declare a destination (qu/tr bind-explicit-property /
    // bind-non-form-display parse NULL when gated on it).
    const markerRoleCollision = (() => {
      if (!this.currentPatternCommand) return false;
      if ((possessiveToken?.normalized ?? '').toLowerCase() !== 'source') return false;
      const schema = (commandSchemas as Record<string, CommandSchema | undefined>)[
        this.currentPatternCommand
      ];
      return !!schema && schema.roles.some(r => r.role === 'source');
    })();
    const isProfilePossessive =
      !!possessiveToken &&
      !!profileMarker &&
      profileMarker !== "'s" &&
      possessiveToken.value === profileMarker &&
      (possessiveToken.kind === 'particle' || possessiveToken.kind === 'punctuation') &&
      !markerRoleCollision;
    if (!isEnglishPossessive && !isProfilePossessive && !splitEnglishPossessive) {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume the possessive marker (`'s`, profile marker, or split `'`)
    if (splitEnglishPossessive) tokens.advance(); // consume the trailing `s` of the split pair

    const propertyToken = tokens.peek();
    if (!propertyToken) {
      tokens.reset(mark);
      return null;
    }

    // English keeps the historical selector-or-identifier property (e.g.
    // `#element's *opacity`). For profile markers, only an identifier is a
    // property — otherwise a target+patient construct like `#button の .active`
    // ("toggle .active on #button") would be mis-read as a property path.
    // A `keyword` property is also valid for the English `'s` (whole or split):
    // some property names translate to a profile KEYWORD rather than a bare
    // identifier (vi `value` → `giá trị`, a single keyword token), so without this
    // the English-possessive `#picker's giá trị` lost its property and fell back to a
    // bare `#picker` selector — mismatching the en reference's property-path.
    const propertyOk =
      propertyToken.kind === 'identifier' ||
      ((isEnglishPossessive || splitEnglishPossessive) &&
        (propertyToken.kind === 'selector' || propertyToken.kind === 'keyword'));
    if (!propertyOk) {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume property

    // Create property-path: #element's *opacity (property normalized to English:
    // `#picker's 値`/`giá trị` → `#picker's value`).
    return createPropertyPath(
      createSelector(token.value),
      this.toEnglishProperty(propertyToken.value)
    );
  }

  /**
   * Try to match a selector + property expression like "#output.innerText".
   * This handles cases where the tokenizer produces two selector tokens:
   * - #output (id selector)
   * - .innerText (looks like class selector, but is actually property)
   *
   * Pattern: id-selector + class-selector-that-is-actually-property
   * Returns a property-path value if matched, or null if not.
   */
  private tryMatchSelectorPropertyExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token || token.kind !== 'selector') return null;

    // Must be an ID selector (starts with #)
    if (!token.value.startsWith('#')) return null;

    // Look ahead for: selector that looks like a property (.something)
    const mark = tokens.mark();
    tokens.advance(); // consume first selector

    const propertyToken = tokens.peek();
    if (!propertyToken || propertyToken.kind !== 'selector') {
      tokens.reset(mark);
      return null;
    }

    // Second token must look like a class selector (starts with .)
    // but we interpret it as a property access
    if (!propertyToken.value.startsWith('.')) {
      tokens.reset(mark);
      return null;
    }

    // Verify the next token is not a selector (to avoid consuming too many)
    // This helps distinguish "#output.innerText" from "#box .child"
    const peek2 = tokens.peek(1);
    if (peek2 && peek2.kind === 'selector') {
      // Could be a compound selector chain - only take first two
    }

    tokens.advance(); // consume property selector

    // Create property-path: #output.innerText
    // Extract property name without the leading dot
    const propertyName = propertyToken.value.slice(1);

    return createPropertyPath(createSelector(token.value), propertyName);
  }

  /**
   * Match a group pattern token (optional sequence).
   */
  private matchGroupToken(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'group' },
    captured: Map<SemanticRole, SemanticValue>,
    nextPatternToken?: PatternToken
  ): boolean {
    const mark = tokens.mark();

    // Track which roles were captured before this group
    const capturedBefore = new Set(captured.keys());

    const success = this.matchTokenSequence(
      tokens,
      patternToken.tokens,
      captured,
      nextPatternToken
    );

    if (!success) {
      tokens.reset(mark);
      // Clear any roles that were partially captured during the failed group match
      for (const role of captured.keys()) {
        if (!capturedBefore.has(role)) {
          captured.delete(role);
        }
      }
      return patternToken.optional || false;
    }

    return true;
  }

  /**
   * Get the type of match for a token against a value.
   * Used for confidence calculation.
   */
  private getMatchType(
    token: LanguageToken,
    value: string
  ): 'exact' | 'normalized' | 'stem' | 'case-insensitive' | 'none' {
    // Exact match (highest confidence)
    if (token.value === value) return 'exact';

    // Explicit keyword map normalized match (high confidence)
    if (token.normalized === value) return 'normalized';

    // Morphologically normalized stem match (medium-high confidence)
    // Only accept if stem confidence is reasonable
    if (token.stem === value && token.stemConfidence !== undefined && token.stemConfidence >= 0.7) {
      return 'stem';
    }

    // Case-insensitive match for keywords (medium confidence)
    if (token.kind === 'keyword' && token.value.toLowerCase() === value.toLowerCase()) {
      return 'case-insensitive';
    }

    // Diacritic-insensitive match for keywords (medium confidence). Arabic
    // harakat are OPTIONAL in the script — `بدّل` and `بَدِّل` are the same word,
    // and a pattern literal carries whichever spelling the profile declared. The
    // keyword map is queried diacritic-insensitively, so the token is correctly
    // recognized; without this the surface still fails to match its own literal
    // here, one layer later. Restricted to keywords, and `stripOptionalDiacritics`
    // touches only Arabic-exclusive codepoints, so it is inert for every other
    // script (Latin accents are meaning-bearing and must never be stripped).
    if (token.kind === 'keyword') {
      const strippedToken = stripOptionalDiacritics(token.value).toLowerCase();
      if (strippedToken && strippedToken === stripOptionalDiacritics(value).toLowerCase()) {
        return 'case-insensitive';
      }
    }

    return 'none';
  }

  /**
   * Track stem matches for confidence calculation.
   * This is set during matching and read during confidence calculation.
   */
  private stemMatchCount: number = 0;
  private totalKeywordMatches: number = 0;

  // ==========================================================================
  // Depth Limits for Expression Parsing (security hardening)
  // ==========================================================================

  /** Maximum depth for nested property access (e.g., a.b.c.d...) */
  private static readonly MAX_PROPERTY_DEPTH = 10;

  /** Maximum number of arguments in method calls */
  private static readonly MAX_METHOD_ARGS = 20;

  /**
   * Commands whose primary argument is a DECLARATION signature `name(params)`, not
   * a call expression — the bare-call fold must not consume the `(params)` for
   * these (see the fold call site in matchRoleToken).
   */
  private static readonly DECLARATION_COMMANDS = new Set(['behavior', 'def', 'install']);

  /**
   * Convert a language token to a semantic value.
   */
  private tokenToSemanticValue(token: LanguageToken): SemanticValue | null {
    switch (token.kind) {
      case 'selector':
        return createSelector(token.value);

      case 'literal':
        return this.parseLiteralValue(token.value);

      case 'keyword':
        // Keywords might be references or values
        const lower = (token.normalized || token.value).toLowerCase();
        if (isValidReference(lower)) {
          return createReference(lower);
        }
        return createLiteral(token.normalized || token.value);

      case 'identifier':
        // Canonical `@attr` typing: an attribute reference is a selector no
        // matter which slot captures it (mirrors semantic-parser's own
        // tokenToSemanticValue and the css-selector extractor). The former
        // slot-dependent reading typed the SAME token differently between
        // en's patterns and the lax generated event-role slots.
        if (token.value.startsWith('@')) {
          return createSelector(token.value);
        }
        // Check if it's a variable reference (:local or $global)
        // Note: these don't match the ReferenceValue union but are used as a
        // reference token downstream — this cast preserves existing behavior
        if (token.value.startsWith(':') || token.value.startsWith('$')) {
          return createReference(token.value as ReferenceValue['value']);
        }
        // Check if it's a built-in reference
        const identLower = token.value.toLowerCase();
        if (isValidReference(identLower)) {
          return createReference(identLower);
        }
        // Regular identifiers are variable references - use 'expression' type
        // which gets converted to 'identifier' AST nodes by semantic-integration.ts
        return { type: 'expression', raw: token.value } as const;

      case 'url':
        // URLs are treated as string literals (paths/URLs for navigation/fetch)
        return createLiteral(token.value, 'string');

      default:
        return null;
    }
  }

  /**
   * Parse a literal value (string, number, boolean).
   */
  private parseLiteralValue(value: string): SemanticValue {
    // String literal
    if (
      value.startsWith('"') ||
      value.startsWith("'") ||
      value.startsWith('`') ||
      value.startsWith('「')
    ) {
      const inner = value.slice(1, -1);
      return createLiteral(inner, 'string');
    }

    // Boolean
    if (value === 'true') return createLiteral(true, 'boolean');
    if (value === 'false') return createLiteral(false, 'boolean');

    // Duration (number with suffix)
    const durationMatch = value.match(/^(\d+(?:\.\d+)?)(ms|s|m|h)?$/);
    if (durationMatch) {
      const num = parseFloat(durationMatch[1]);
      const unit = durationMatch[2];
      if (unit) {
        return createLiteral(value, 'duration');
      }
      return createLiteral(num, 'number');
    }

    // Plain number
    const num = parseFloat(value);
    if (!isNaN(num)) {
      return createLiteral(num, 'number');
    }

    // Default to string
    return createLiteral(value, 'string');
  }

  /**
   * Apply extraction rules to fill in static values and defaults for missing
   * roles, and re-shape captured roles that declare a `transform` (the
   * ExtractionRule field was documented but unwired — go-qu-url-dest is the
   * first consumer: it re-types the marker-bound capture to the expression
   * shape the en reference produces).
   */
  private applyExtractionRules(
    pattern: LanguagePattern,
    captured: Map<SemanticRole, SemanticValue>
  ): void {
    for (const [role, rule] of Object.entries(pattern.extraction)) {
      const existing = captured.get(role as SemanticRole);
      if (existing) {
        if (rule.transform) {
          const raw =
            existing.type === 'literal'
              ? String(existing.value)
              : String((existing as { raw?: unknown }).raw ?? '');
          captured.set(role as SemanticRole, rule.transform(raw));
        }
      } else if (rule.value !== undefined) {
        // Static value extraction (e.g., action: { value: "toggle" })
        captured.set(role as SemanticRole, { type: 'literal', value: rule.value });
      } else if (rule.default) {
        captured.set(role as SemanticRole, rule.default);
      }
    }
  }

  /**
   * Check if a pattern token is optional.
   */
  private isOptional(patternToken: PatternToken): boolean {
    return patternToken.type !== 'literal' && patternToken.optional === true;
  }

  // Confidence scoring delegated to ConfidenceModel (Phase 3.3)
  // See confidence-model.ts for DefaultConfidenceModel implementation

  // ===========================================================================
  // English Idiom Support - Noise Word Handling
  // ===========================================================================

  /**
   * Noise words that can be skipped in English for more natural syntax.
   * - "the" before selectors: "toggle the .active" → "toggle .active"
   * - "the" before identifiers: "set the color to red" → "set color to red"
   * - "class" after class selectors: "add the .visible class" → "add .visible"
   */
  private static readonly ENGLISH_NOISE_WORDS = new Set(['the', 'a', 'an']);

  /**
   * Skip noise words like "the" before selectors and identifiers.
   * This enables more natural English syntax like "toggle the .active"
   * and "set the color to red".
   */
  private skipNoiseWords(tokens: TokenStream): void {
    const token = tokens.peek();
    if (!token) return;

    const tokenLower = token.value.toLowerCase();

    // Check if current token is a noise word (like "the")
    if (PatternMatcher.ENGLISH_NOISE_WORDS.has(tokenLower)) {
      // Look ahead to see if the next token is a selector or identifier
      const mark = tokens.mark();
      tokens.advance();
      const nextToken = tokens.peek();

      if (nextToken && (nextToken.kind === 'selector' || nextToken.kind === 'identifier')) {
        // Keep the position after "the" - effectively skipping it
        return;
      }

      // `the <ref>.<prop>` where the reference base tokenizes as a KEYWORD and
      // the property as a fused `.`-selector (`the target .closest ("li")` in
      // the non-English tokenizers — en fuses `target.closest` into one
      // identifier and takes the branch above). Without the skip the role slot
      // captures the bare article and the pattern dies on the following
      // keyword (behavior-sortable's `set item to the target.closest("li")`).
      // Gated on the property-access SHAPE (keyword + `.`-selector), so `the
      // <event-noun> <marker>` keeps the leaked-article handling below.
      const afterNext = tokens.peek(1);
      if (
        nextToken &&
        nextToken.kind === 'keyword' &&
        afterNext &&
        afterNext.kind === 'selector' &&
        /^\.[a-zA-Z_]/.test(afterNext.value)
      ) {
        return;
      }

      // "the next <sel>" / "the closest .modal": the article also precedes
      // positional-phrase heads, which tokenize as keywords/literals — skip it
      // so tryMatchPositionalExpression sees the positional keyword first.
      const nextNorm = nextToken ? (nextToken.normalized ?? nextToken.value).toLowerCase() : '';
      if (nextToken && PatternMatcher.POSITIONAL_OR_SCOPE_KEYWORDS.has(nextNorm)) {
        return;
      }

      // Leaked English article before a FRONTED reference-noun patient: in a
      // non-English parse `the` is never authored — it's a dict/transformer leak
      // (hi `the घटना को …` = "the event", the fronted halt patient). Without
      // skipping it the patient role grabs only `the` and the following marker
      // fails, so `halt-event-hi-sov-patient-first` (`{patient} को {event} पर
      // रोकें`) never matches and the halt loses its patient (a bare halt stops
      // the handler). The reference noun (घटना→event) then fills the patient, so
      // `halt the event` continues.
      //
      // Fires for a fronted/leaked-article reference-noun patient when the noun is
      // followed by a clause boundary, NOT a command verb:
      //   - a particle marker   (SOV `the घटना को …`  → reference="event")
      //   - a sequence/conjunction keyword or end-of-stream (SVO `the evento
      //     entonces …`, `the подія тоді …` → reference="event")
      // It deliberately does NOT fire for `the <ref-noun> <verb>` (tr
      // form-submit-prevent's `the olay çağır …` = "the event call …", ref noun
      // followed by the `call` verb): skipping `the` there breaks tr's fragile
      // body parse (the §7y regression). Before the boundary cases were added, the
      // SVO/VSO translations kept the leaked `the` as the patient
      // (`patient:expression="the"`), mismatching both the en reference and the SOV
      // parse — part of the `halt.patient` R1 residue. See STRUCTURAL_ARCS_ROADMAP.md
      // (hi halt-propagation).
      const afterRefNoun = tokens.peek(1);
      const afterRefNorm = afterRefNoun
        ? (afterRefNoun.normalized ?? afterRefNoun.value).toLowerCase()
        : '';
      // In VERB-FIRST word orders (SVO/VSO/V2) a ref-noun followed by a
      // COMMAND VERB is also a clause boundary: the verb begins the NEXT
      // juxtaposed body command (`detener the evento llamar validateForm()…`
      // — evento ends the halt clause, llamar opens the call). Without it the
      // patient captured the bare article (`patient:expression="the"`, the
      // halt.patient R1 residue across es/it/de/pl/zh + the behaviors ×17).
      // Deliberately NOT applied to SOV profiles: there the fronted patient's
      // own verb comes LATER in the clause, so a following verb is NOT a
      // boundary (tr `the olay çağır …` — the §7y regression this branch's
      // gate originally protected).
      const verbBoundary =
        this.currentProfile?.wordOrder !== 'SOV' &&
        PatternMatcher.COMMAND_ACTION_KEYWORDS.has(afterRefNorm);
      const refNounFollowedByBoundary =
        !afterRefNoun ||
        afterRefNoun.kind === 'particle' ||
        PatternMatcher.CLAUSE_BOUNDARY_KEYWORDS.has(afterRefNorm) ||
        verbBoundary;
      if (
        nextToken &&
        nextToken.kind === 'keyword' &&
        this.currentProfile?.code !== 'en' &&
        isValidReference(nextNorm) &&
        refNounFollowedByBoundary
      ) {
        return;
      }

      // English authored `the` before a reference word ("halt the event"). The
      // reference (`event`, `result`, `target`, …) tokenizes as a `keyword`, so
      // the selector/identifier skip (case 1) and the positional skip (case 2)
      // both miss it, and the role would otherwise capture only the article
      // (`halt the event` → patient:literal="the", dropping the event). Skip it so
      // the canonical reference fills the role (patient:reference="event"). This
      // matches what the SOV branch above already produces for the leaked-article
      // translations (`the घटना को` → reference="event"); before this, en was the
      // odd reference (literal="the") that every faithful SOV parse mismatched —
      // the dominant `halt.patient` R1 residue. en-only and gated to a valid
      // reference: an English noun ("the color") is an identifier (case 1) and a
      // non-reference keyword ("the default") is left untouched.
      if (
        nextToken &&
        nextToken.kind === 'keyword' &&
        this.currentProfile?.code === 'en' &&
        isValidReference(nextNorm)
      ) {
        return;
      }

      // Not followed by a selector, identifier, or positional keyword — revert
      tokens.reset(mark);
    }

    // Also handle "class" after class selectors: ".visible class" → ".visible"
    // This is handled when the selector has already been consumed,
    // so we check if current token is "class" and skip it
    if (tokenLower === 'class') {
      // Skip "class" as it's just noise after a class selector
      tokens.advance();
    }
  }

  /**
   * Extract event modifiers from the token stream.
   * Event modifiers are .once, .debounce(N), .throttle(N), .queue(strategy)
   * that can appear after event names.
   *
   * Returns EventModifiers object or undefined if no modifiers found.
   */
  extractEventModifiers(tokens: TokenStream): import('../types').EventModifiers | undefined {
    const modifiers: {
      once?: boolean;
      debounce?: number;
      throttle?: number;
      queue?: 'first' | 'last' | 'all' | 'none';
      from?: SemanticValue;
    } = {};

    let foundModifier = false;

    // Consume all consecutive event modifier tokens
    while (!tokens.isAtEnd()) {
      const token = tokens.peek();
      if (!token || token.kind !== 'event-modifier') {
        break;
      }

      const metadata = token.metadata as
        { modifierName: string; value?: number | string } | undefined;
      if (!metadata) {
        break;
      }

      foundModifier = true;

      switch (metadata.modifierName) {
        case 'once':
          modifiers.once = true;
          break;
        case 'debounce':
          if (typeof metadata.value === 'number') {
            modifiers.debounce = metadata.value;
          }
          break;
        case 'throttle':
          if (typeof metadata.value === 'number') {
            modifiers.throttle = metadata.value;
          }
          break;
        case 'queue':
          if (
            metadata.value === 'first' ||
            metadata.value === 'last' ||
            metadata.value === 'all' ||
            metadata.value === 'none'
          ) {
            modifiers.queue = metadata.value;
          }
          break;
      }

      tokens.advance();
    }

    return foundModifier ? modifiers : undefined;
  }
}

// =============================================================================
// Convenience Functions
// =============================================================================

/**
 * Singleton pattern matcher instance.
 */
export const patternMatcher = new PatternMatcher();

/**
 * Match tokens against a pattern.
 */
export function matchPattern(
  tokens: TokenStream,
  pattern: LanguagePattern
): PatternMatchResult | null {
  return patternMatcher.matchPattern(tokens, pattern);
}

/**
 * Match tokens against multiple patterns, return best match.
 */
export function matchBest(
  tokens: TokenStream,
  patterns: LanguagePattern[]
): PatternMatchResult | null {
  return patternMatcher.matchBest(tokens, patterns);
}
